/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { capitalize } from 'lodash';
import { Check, RotateCcw, X } from 'lucide-react';
import {
  CorrectReleaseRequestEditsInnerOpEnum,
  CorrectRecordingRequestEditsInnerOpEnum,
  CreateReleaseAndLinkRequestSourceEnum,
  CreateArtistAndLinkRequestEditsInnerSourceEnum,
  CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum,
  CreateArtistAndLinkRequestEditsInnerOpEnum,
  ChangeAssociatedArtistsRequestEditsInnerOpEnum,
  ManageContributionsRequestEditsInnerOpEnum,
  RecordingWestburyContributionsInner,
} from '../../../../../shared_components/generated/admin';
import type {
  CorrectRecordingRequestEditsInner,
  ChangeAssociatedArtistsRequestEditsInner,
  CreateArtistAndLinkRequestEditsInner,
  ManageContributionsRequestEditsInner,
  RecordingManualChangesResponse,
  ReleaseManualChangesResponse,
} from '../../../../../shared_components/generated/admin';
import {
  LookupLookupReleaseResponseSourceEnum,
  LookupSourceArtistLookupSourceArtistResponseSourceEnum,
} from '../../../../../shared_components/generated/admin/apis/PortalApi';
import type { Row } from '../MasterDiscographyTypes';
import { MANUAL_CHANGE_LINE_CLASS } from '../manualChangeIndicator';
import { friendlySaveError } from './saveErrorMessage';
import { mainArtistDisplayName } from 'shared_components/utils/mainArtist';
import type { MainArtistItem } from 'shared_components/utils/mainArtist';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import WModal from 'shared_components/components/WModal';
import { useApi } from 'shared_components/context';

// Artist with featured status and optional id (from WMC artist lookup)
type FeaturedStatus = 'fa' | 'ofa' | 'nf';
type ArtistMember = {
  id: string;
  stageName: string;
  // True when this member already exists in the WMC graph and `id` is the
  // internal WMC id; false when `id` is the source-side id.
  exists?: boolean;
};
type ArtistEntry = {
  name: string;
  isFeatured: boolean;
  featuredStatus: FeaturedStatus;
  id?: string;
  members?: ArtistMember[];
  type?: 'persona' | 'group';
  // When set, this artist hasn't been created yet — on save, call /create instead
  // of including it in the /artists payload.
  pendingCreate?: {
    source: 'discogs' | 'MB';
    sourceId: string;
  };
};

// ── Manually-edited indicators (sourced from contract § 4.5.2 / 4.5.3) ──

/** Class applied to any element (chip, pill) that carries a manual change.
 *  Adds the purple left line via a ::before pseudo-element so it stays at full
 *  height regardless of border-radius / padding / disabled state. Shared with
 *  the discography row indicator — see manualChangeIndicator.ts / global.css. */
const EDITED_LEFT_BORDER_CLASS = MANUAL_CHANGE_LINE_CLASS;
const MANUAL_CHANGE_TOOLTIP = 'Manually Changed';

// ── Shared form-field styling ──
// Text inputs are the source of truth for field appearance. Selects reuse the
// same background, border, padding, text size and disabled treatment so the
// two control types are visually identical (including height). The empty
// "default" option is rendered in the placeholder colour (gray-400) via
// `selectTextClass`, matching input placeholders (see global.css).
const SELECT_BASE_CLASS =
  'wmc-select tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-pl-3 tw-py-2 tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800';

/** White text when a real value is selected; muted placeholder colour when the
 *  empty default option is showing. */
const selectTextClass = (hasValue: boolean): string =>
  hasValue ? 'tw-text-white' : 'tw-text-gray-400';

/** Small floating tooltip that appears immediately on hover. Avoids the
 *  browser's native `title` delay. Positioned above the host element via an
 *  absolute child; pointer-events disabled so it never blocks clicks. */
const ManualChangeTooltipBubble: React.FC<{ text: string }> = ({ text }) => (
  <span
    role="tooltip"
    className="tw-absolute tw-bottom-full tw-left-1/2 -tw-translate-x-1/2 tw-mb-1 tw-px-2 tw-py-1 tw-bg-gray-900 tw-text-white tw-text-xs tw-font-medium tw-rounded tw-whitespace-pre-wrap tw-max-w-xs tw-z-50 tw-pointer-events-none tw-shadow-lg tw-border tw-border-gray-700"
  >
    {text}
  </span>
);

/** A purple pulsating dot marking a value that has been changed locally but
 *  not yet saved. Positioned at the top-right of its relatively-positioned
 *  parent. */
const ChangedDot: React.FC = () => (
  <span
    aria-hidden
    title="Unsaved change"
    className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow tw-z-10 tw-pointer-events-none"
  />
);

/** Wraps an `<input>` / `<select>` (which can't host ::before) so the purple
 *  line renders on the wrapper instead. Renders the children unchanged when
 *  not edited/changed so untouched fields keep their original layout.
 *  - `edited`  → persisted manual change: purple left line + hover tooltip.
 *  - `changed` → unsaved local change: purple pulsating dot.
 *  Shows an instant-display tooltip on hover (defaults to "Manually Changed",
 *  but callers can pass a longer string e.g. for the live-note display). */
const ManualChangeWrap: React.FC<{
  edited: boolean;
  changed?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ edited, changed = false, title, children }) => {
  const [hover, setHover] = useState(false);
  if (!edited && !changed) return <>{children}</>;
  return (
    <div
      className={edited ? MANUAL_CHANGE_LINE_CLASS : 'tw-relative'}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {changed && <ChangedDot />}
      {edited && hover && (
        <ManualChangeTooltipBubble text={title ?? MANUAL_CHANGE_TOOLTIP} />
      )}
    </div>
  );
};

/** Wraps a chip / pill so the manual-change line lives on an outer container
 *  whose opacity stays at 1 even when the inner chip dims (disabled state).
 *  Also avoids the pixel offset that any transparent layout-reservation
 *  border on the chip would otherwise push into the absolute pseudo. Shows
 *  an instant-display "Manually Changed" tooltip on hover. */
const ManualChangeChipWrap: React.FC<{
  edited: boolean;
  /** Extra className for the outer wrapper. Use this to preserve the chip's
   *  outer layout flow (e.g. `tw-inline-flex` so the wrapper sits like a
   *  chip in a flex/wrap row). */
  outerClassName?: string;
  children: React.ReactNode;
}> = ({ edited, outerClassName, children }) => {
  const [hover, setHover] = useState(false);
  if (!edited) return <>{children}</>;
  return (
    <div
      className={`${MANUAL_CHANGE_LINE_CLASS} ${
        outerClassName ?? 'tw-inline-flex'
      }`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {hover && <ManualChangeTooltipBubble text={MANUAL_CHANGE_TOOLTIP} />}
    </div>
  );
};

/** WMC-styled radio option — mirrors the shared `WRadioButton` look (hidden
 *  native input + circular ring with a lime fill when selected, lime label
 *  when selected / dove when not) without that component's WForm-context
 *  dependency. */
const WmcRadioOption: React.FC<{
  name: string;
  value: string;
  checked: boolean;
  label: string;
  onChange: () => void;
}> = ({ name, value, checked, label, onChange }) => (
  <label
    className={`tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm ${
      checked ? 'tw-text-surfaceLime' : 'tw-text-textDove'
    }`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="tw-hidden"
    />
    <span
      className={`tw-flex tw-w-5 tw-h-5 tw-shrink-0 tw-border-2 tw-rounded-full ${
        checked ? 'tw-border-surfaceLime' : 'tw-border-textDove'
      }`}
    >
      {checked && (
        <span className="tw-w-2.5 tw-h-2.5 tw-m-[3px] tw-rounded-full tw-bg-surfaceLime" />
      )}
    </span>
    {label}
  </label>
);

/** A multi-value field (label / catalogue number) with the inline add-item UX:
 *  chips with a remove button, a "None" placeholder when empty, and an
 *  "Add {item}" button that swaps to an input + Create/Cancel CTAs. Used in the
 *  Create/Link-release modal so it matches the Edit Release section. */
const MultiValueAddField: React.FC<{
  label: string;
  addLabel: string;
  placeholder: string;
  values: string[];
  onRemove: (index: number) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  commit: () => void;
  inputId?: string;
}> = ({
  label,
  addLabel,
  placeholder,
  values,
  onRemove,
  open,
  setOpen,
  value,
  setValue,
  commit,
  inputId,
}) => (
  <div>
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
      <label
        htmlFor={inputId}
        className="tw-block tw-text-white tw-text-sm tw-font-medium"
      >
        {label}
      </label>
      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">Optional</span>
    </div>
    {values.length > 0 && (
      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
        {values.map((v, index) => (
          <div
            key={index}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
          >
            <span>{v}</span>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
              title="Remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    )}
    {values.length === 0 && (
      <p className="tw-text-gray-400 tw-text-xs tw-mb-2">None</p>
    )}
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      {open ? (
        <>
          <input
            id={inputId}
            type="text"
            autoFocus
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && value.trim()) {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setValue('');
                setOpen(false);
              }
            }}
            className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
          />
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setValue('');
                setOpen(false);
              }}
            />
            <WButton
              variant="primary"
              label="Create"
              disabled={!value.trim()}
              onClick={commit}
            />
          </div>
        </>
      ) : (
        <div className="tw-flex tw-justify-end">
          <button
            type="button"
            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
            onClick={() => {
              setValue('');
              setOpen(true);
            }}
          >
            <i className="wi wi-20px wi-plus tw-mr-1" />
            <span className="tw-underline">{addLabel}</span>
          </button>
        </div>
      )}
    </div>
  </div>
);

// Confidence category styling (same as main table / ContributionPill)
const CONFIDENCE_CATEGORIES: {
  id: string;
  label: string;
  color: string;
  textColor: string;
}[] = [
  {
    id: '1',
    label: 'Verified (No Effort)',
    color: '#6EE038',
    textColor: 'black',
  },
  {
    id: '2',
    label: 'Inferred (Low Effort)',
    color: '#F6FF00',
    textColor: 'black',
  },
  {
    id: '3',
    label: 'Credited (Mid Effort)',
    color: '#FF8A00',
    textColor: 'white',
  },
  {
    id: '4',
    label: 'Unverified (High Effort)',
    color: '#FF2600',
    textColor: 'white',
  },
  {
    id: '5',
    label: 'Unknown (Max Effort)',
    color: '#AAB2B9',
    textColor: 'black',
  },
];

function getContributionPillStyle(categoryId: string): {
  backgroundColor: string;
  color: string;
} {
  const cat =
    CONFIDENCE_CATEGORIES.find((c) => c.id === categoryId) ??
    CONFIDENCE_CATEGORIES[4];
  return { backgroundColor: cat.color, color: cat.textColor };
}

function parseContributionEvidenceNotes(confidencePattern: string): {
  evidenceUrl: string;
  notes: string;
} {
  if (!confidencePattern?.trim()) return { evidenceUrl: '', notes: '' };
  try {
    const o = JSON.parse(confidencePattern) as unknown;
    if (typeof o === 'object' && o && !Array.isArray(o)) {
      const obj = o as Record<string, unknown>;
      return {
        evidenceUrl: typeof obj.evidenceUrl === 'string' ? obj.evidenceUrl : '',
        notes: typeof obj.notes === 'string' ? obj.notes : '',
      };
    }
  } catch {
    // ignore
  }
  return { evidenceUrl: '', notes: '' };
}

const MIN_RELEASE_YEAR = 1920;
const MAX_RELEASE_YEAR_OFFSET = 2; // current year + 2

/** Validate a year-of-release string. Returns an error message or null if valid.
 *  Empty/blank values are allowed (field is optional). */
function validateReleaseYear(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null; // optional field
  if (!/^\d{4}$/.test(trimmed)) return 'Year must be a 4-digit number';
  const year = Number(trimmed);
  const maxYear = new Date().getFullYear() + MAX_RELEASE_YEAR_OFFSET;
  if (year < MIN_RELEASE_YEAR)
    return `Year cannot be before ${MIN_RELEASE_YEAR}`;
  if (year > maxYear) return `Year cannot be after ${maxYear}`;
  return null;
}

/** Validate an evidence URL. Returns an error message or null if valid.
 *  The field is optional — empty/blank values pass. A non-empty value must be
 *  a browser-usable absolute URL with an http(s) scheme and a real host. */
function validateEvidenceUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null; // optional field
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'Enter a valid URL starting with http:// or https://';
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'URL must start with http:// or https://';
  }
  // Require a dotted host (e.g. example.com) so partial inputs like
  // "https://foo" are rejected.
  if (!parsed.hostname || !parsed.hostname.includes('.')) {
    return 'Enter a valid URL (e.g. https://example.com)';
  }
  return null;
}

/** Check for case-insensitive duplicates in a list. Returns the first duplicate found or null. */
function findDuplicate(values: string[]): string | null {
  const seen = new Set<string>();
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) return v;
    seen.add(key);
  }
  return null;
}

type ReleaseSourceForm =
  | 'Discogs'
  | 'Deezer'
  | 'Spotify'
  | 'MusicBrainz'
  | 'PPL'
  | 'Other';

function mapReleaseSourceToLookupEnum(
  source: ReleaseSourceForm
):
  | (typeof LookupLookupReleaseResponseSourceEnum)[keyof typeof LookupLookupReleaseResponseSourceEnum]
  | null {
  switch (source) {
    case 'Discogs':
      return LookupLookupReleaseResponseSourceEnum.Discogs;
    case 'Deezer':
      return LookupLookupReleaseResponseSourceEnum.Deezer;
    case 'Spotify':
      return LookupLookupReleaseResponseSourceEnum.Spotify;
    case 'MusicBrainz':
      return LookupLookupReleaseResponseSourceEnum.Mb;
    case 'PPL':
      return LookupLookupReleaseResponseSourceEnum.Ppl;
    default:
      return null;
  }
}

function mapReleaseSourceToCreateEnum(
  source: ReleaseSourceForm
): CreateReleaseAndLinkRequestSourceEnum {
  switch (source) {
    case 'Discogs':
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
    case 'Deezer':
      return CreateReleaseAndLinkRequestSourceEnum.Deezer;
    case 'Spotify':
      return CreateReleaseAndLinkRequestSourceEnum.Spotify;
    case 'MusicBrainz':
      return CreateReleaseAndLinkRequestSourceEnum.Mb;
    case 'PPL':
      return CreateReleaseAndLinkRequestSourceEnum.Ppl;
    case 'Other':
      return CreateReleaseAndLinkRequestSourceEnum.Other;
    default:
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
  }
}

const CATALOG_SOURCES: ReleaseSourceForm[] = [
  'Discogs',
  'Deezer',
  'Spotify',
  'MusicBrainz',
  'PPL',
];

/** ISRCs reassigned from other recordings during this save. */
export interface ReassignedIsrc {
  /** The ISRC that was moved to the current recording. */
  isrc: string;
  /** Recording IDs that previously held this ISRC. */
  fromRecordingIds: string[];
}

export interface RecordingEditFormProps {
  row: Row | null;
  onSave: (row: Row, reassignedIsrcs?: ReassignedIsrc[]) => void;
  onCancel: () => void;
  /** When true, release and recording sections stack in one column (e.g. for side panel). */
  stacked?: boolean;
  /** When true, do not render the footer (e.g. when used in panel with external fixed footer). */
  hideFooter?: boolean;
  /** When true, show only the Release section (hide Recording & Artist section). */
  releaseOnly?: boolean;
  /** User ID (required for release lookup in Change Release modal). */
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
}

export interface RecordingEditFormHandle {
  save: () => void;
  cancel: () => void;
  /** True when the recording or release section has unsaved changes. Used by
   *  the parent to confirm before switching to a different recording. */
  isDirty: () => boolean;
}

export const RecordingEditForm = forwardRef<
  RecordingEditFormHandle,
  RecordingEditFormProps
>(function RecordingEditForm(
  {
    row,
    onSave,
    onCancel,
    stacked = false,
    hideFooter = false,
    releaseOnly = false,
    userId,
    availableFormats = [],
    availableTypes = [],
    availableContributions = [],
    availableArtists = [],
  },
  ref
) {
  const { adminApi: api } = useApi();
  const [formData, setFormData] = useState<Partial<Row>>({});
  const [labels, setLabels] = useState<string[]>([]);
  const [catNumbers, setCatNumbers] = useState<string[]>([]);
  const [versions, setVersions] = useState<string[]>([]);
  const [isrcs, setIsrcs] = useState<string[]>([]);
  const [artists, setArtists] = useState<ArtistEntry[]>([]);
  const [contributions, setContributions] = useState<
    RecordingWestburyContributionsInner[]
  >([]);
  const [showReleaseCorrectionModal, setShowReleaseCorrectionModal] =
    useState(false);
  const [showChangeReleaseModal, setShowChangeReleaseModal] = useState(false);
  const [releaseId, setReleaseId] = useState('');
  const [releaseSource, setReleaseSource] = useState<
    'Discogs' | 'Deezer' | 'Spotify' | 'MusicBrainz' | 'PPL' | 'Other'
  >('Discogs');
  const [sourceUrl, setSourceUrl] = useState('');
  const [newReleaseTitle, setNewReleaseTitle] = useState('');
  const [newReleaseYear, setNewReleaseYear] = useState('');
  const [newReleaseLabels, setNewReleaseLabels] = useState<string[]>([]);
  const [newReleaseCatNumbers, setNewReleaseCatNumbers] = useState<string[]>(
    []
  );
  // Inline add-item inputs for the Create/Link-release modal's label and
  // catalogue-number fields (shared across the not-found and new-release
  // branches, which never render simultaneously).
  const [newLabelInputOpen, setNewLabelInputOpen] = useState(false);
  const [newLabelInputValue, setNewLabelInputValue] = useState('');
  const [newCatNoInputOpen, setNewCatNoInputOpen] = useState(false);
  const [newCatNoInputValue, setNewCatNoInputValue] = useState('');
  const [newReleaseFormat, setNewReleaseFormat] = useState('');
  const [newReleaseType, setNewReleaseType] = useState('');
  const [isReleaseEditable, setIsReleaseEditable] = useState(false);
  // The recording section is always editable (no "Edit Recording Details"
  // step); Save/Cancel CTAs appear only once something changes. The linked
  // release section keeps its own explicit edit step (isReleaseEditable).
  const isRecordingEditable = true;
  const [recordingSaveLoading, setRecordingSaveLoading] = useState(false);
  // Friendly error messages shown above the Save CTA when a save API call fails.
  const [recordingSaveError, setRecordingSaveError] = useState<string | null>(
    null
  );
  const [releaseSaveError, setReleaseSaveError] = useState<string | null>(null);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<{
    type: 'section' | 'field';
    field?: string;
  } | null>(null);
  const [releaseLookupLoading, setReleaseLookupLoading] = useState(false);
  const [releaseLookupError, setReleaseLookupError] = useState<string | null>(
    null
  );
  const [releaseLookupSuccess, setReleaseLookupSuccess] = useState(false);
  const [releaseLookupNotFound, setReleaseLookupNotFound] = useState(false);
  const [releaseLookupNotLinked, setReleaseLookupNotLinked] = useState(false);
  const [releaseNotLinkedConfirmed, setReleaseNotLinkedConfirmed] =
    useState(false);
  const [lookupReleaseId, setLookupReleaseId] = useState('');
  const [lookupEdgeId, setLookupEdgeId] = useState<string | null>(null);
  const [lookupDateReleased, setLookupDateReleased] = useState<string | null>(
    null
  );
  const [releaseChangeSubmitLoading, setReleaseChangeSubmitLoading] =
    useState(false);
  const [releaseChangeSubmitError, setReleaseChangeSubmitError] = useState<
    string | null
  >(null);
  const [releaseSameAsCurrentError, setReleaseSameAsCurrentError] =
    useState(false);
  const [yearError, setYearError] = useState<string | null>(null);
  const [newReleaseYearError, setNewReleaseYearError] = useState<string | null>(
    null
  );
  const [showLiveToYesModal, setShowLiveToYesModal] = useState(false);
  const [liveToYesNote, setLiveToYesNote] = useState('');
  const [isrcFormatError, setIsrcFormatError] = useState<string | null>(null);
  // Inline "add item" inputs (ISRC / Label / Catalogue Number). The input is
  // hidden until the user clicks "Add {item}"; while open, the Add button is
  // replaced with Create / Cancel CTAs. Tracked so "Save Changes" can warn
  // when an item is still being entered (not yet created/cancelled).
  const [isrcInputOpen, setIsrcInputOpen] = useState(false);
  const [isrcInputValue, setIsrcInputValue] = useState('');
  const [labelInputOpen, setLabelInputOpen] = useState(false);
  const [labelInputValue, setLabelInputValue] = useState('');
  const [catNoInputOpen, setCatNoInputOpen] = useState(false);
  const [catNoInputValue, setCatNoInputValue] = useState('');
  // When a save is attempted while an inline add-item input is still open,
  // this holds the item name ("ISRC" / "label" / "catalogue number") so the
  // warning modal can tell the user to create or cancel it first.
  const [unsavedItemWarning, setUnsavedItemWarning] = useState<string | null>(
    null
  );
  const [pendingDuplicateIsrc, setPendingDuplicateIsrc] = useState<
    string | null
  >(null);
  const [pendingDuplicateMatches, setPendingDuplicateMatches] = useState<
    {
      recordingId: string;
      recordingTitle: string;
      artistName: string;
      documentId: string;
    }[]
  >([]);
  // Maps ISRC → all matching recordings it's being reassigned from
  const [confirmedDuplicateIsrcs, setConfirmedDuplicateIsrcs] = useState<
    Record<string, { recordingId: string; documentId: string }[]>
  >({});
  const [showCreateArtist, setShowCreateArtist] = useState(false);
  const [newArtistAlias, setNewArtistAlias] = useState('');
  const [newArtistFeatured, setNewArtistFeatured] =
    useState<FeaturedStatus>('fa');
  const [newArtistSource, setNewArtistSource] = useState<'discogs' | 'MB'>(
    'discogs'
  );
  const [newArtistSourceId, setNewArtistSourceId] = useState('');
  const [newArtistIsGroup, setNewArtistIsGroup] = useState(false);
  const [newArtistMembers, setNewArtistMembers] = useState<
    { id: string; stageName: string; exists: boolean }[]
  >([]);
  // Whether the looked-up artist itself already exists in the WMC graph.
  const [newArtistExists, setNewArtistExists] = useState(false);
  const [newArtistMemberSearch, setNewArtistMemberSearch] = useState('');
  const [newArtistMemberResults, setNewArtistMemberResults] = useState<
    { id: string; stageName: string }[]
  >([]);
  const [newArtistMemberSearchLoading, setNewArtistMemberSearchLoading] =
    useState(false);
  const [newArtistSourceLookupLoading, setNewArtistSourceLookupLoading] =
    useState(false);
  const [newArtistSourceLookupError, setNewArtistSourceLookupError] = useState<
    string | null
  >(null);
  const [pendingArtistName, setPendingArtistName] = useState<string | null>(
    null
  );
  const [pendingArtistId, setPendingArtistId] = useState<string | null>(null);
  const [pendingArtistMembers, setPendingArtistMembers] = useState<
    ArtistMember[]
  >([]);
  const [pendingArtistFeatured, setPendingArtistFeatured] =
    useState<FeaturedStatus>('fa');
  const [editingVersionIndex, setEditingVersionIndex] = useState<number | null>(
    null
  );
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [artistLookupResults, setArtistLookupResults] = useState<
    { id: string; stageName: string; members?: ArtistMember[] }[]
  >([]);
  const [artistLookupLoading, setArtistLookupLoading] = useState(false);
  const [artistsError, setArtistsError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [labelsError, setLabelsError] = useState<string | null>(null);
  const [catNumbersError, setCatNumbersError] = useState<string | null>(null);
  const [artistActionModalIndex, setArtistActionModalIndex] = useState<
    number | null
  >(null);
  const [artistActionConfirm, setArtistActionConfirm] = useState<
    'demote' | 'remove' | null
  >(null);
  const [demotedArtists, setDemotedArtists] = useState<ArtistEntry[]>([]);
  const [showRemoveContributionModal, setShowRemoveContributionModal] =
    useState<number | null>(null);
  const [addContributionDropdownOpen, setAddContributionDropdownOpen] =
    useState(false);
  const [contributionSearchQuery, setContributionSearchQuery] = useState('');
  const [contributionCategoryOpenIndex, setContributionCategoryOpenIndex] =
    useState<number | null>(null);
  const [categoryDropdownRect, setCategoryDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionModalContext, setContributionModalContext] = useState<
    | { type: 'edit'; index: number }
    | { type: 'add'; contributionValue: string }
    | null
  >(null);
  const [contributionModalForm, setContributionModalForm] = useState<{
    contributionName: string;
    confidenceCategory: string;
    evidenceUrl: string;
    notes: string;
    personaId: string;
  }>({
    contributionName: '',
    confidenceCategory: '1',
    evidenceUrl: '',
    notes: '',
    personaId: '',
  });
  const [contributionModalEvidenceError, setContributionModalEvidenceError] =
    useState<string | null>(null);
  const [clientAliases, setClientAliases] = useState<
    { stageName: string; personaId: string }[]
  >([]);
  const [clientAliasesLoading, setClientAliasesLoading] = useState(false);
  const [releaseSaveLoading, setReleaseSaveLoading] = useState(false);
  // Manual-change overlays (contract § 4.5.2 / 4.5.3). Populated when the
  // modal opens for a row that has manual edits.
  const [editedRecording, setEditedRecording] =
    useState<RecordingManualChangesResponse | null>(null);
  const [editedRelease, setEditedRelease] =
    useState<ReleaseManualChangesResponse | null>(null);
  // Bumped after every successful save so the manual-change overlay fetch
  // re-runs without needing to close + reopen the modal.
  const [editsVersion, setEditsVersion] = useState(0);
  const [originalValues, setOriginalValues] = useState<{
    formData: Partial<Row>;
    labels: string[];
    catNumbers: string[];
    versions: string[];
    isrcs: string[];
    artists: ArtistEntry[];
    contributions: RecordingWestburyContributionsInner[];
  } | null>(null);

  useEffect(() => {
    const validCount = artists.filter((a) => a.name.trim()).length;
    if (validCount > 0 && artistsError) setArtistsError(null);
  }, [artists, artistsError]);

  // Fetch the manual-changes overlay for the recording + release on modal
  // open (contract 4.5.2 / 4.5.3). Failures are non-fatal — the form just
  // renders without the purple-line / dot indicators.
  useEffect(() => {
    if (!row?.id || !userId) {
      setEditedRecording(null);
      setEditedRelease(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const recordingPromise = api
        .recordingManualChangesRecordingManualChangesResponse({
          userId,
          parentRecordingId: row.id,
          documentId: row.documentId,
        })
        .catch((err: unknown) => {
          console.warn('recording manual-changes lookup failed', err);
          return null;
        });
      const releasePromise = row.earliestParentReleaseId
        ? api
            .releaseManualChangesReleaseManualChangesResponse({
              userId,
              releaseId: row.earliestParentReleaseId,
              recordingId: row.id,
              documentId: row.documentId,
            })
            .catch((err: unknown) => {
              console.warn('release manual-changes lookup failed', err);
              return null;
            })
        : Promise.resolve(null);
      const [recordingResp, releaseResp] = await Promise.all([
        recordingPromise,
        releasePromise,
      ]);
      if (cancelled) return;
      setEditedRecording(recordingResp ?? null);
      setEditedRelease(releaseResp ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    userId,
    row?.id,
    row?.documentId,
    row?.earliestParentReleaseId,
    editsVersion,
  ]);

  // Helper lookups derived from the manual-changes responses ──────────────
  const editedRecordingFields = editedRecording?.fields ?? {};
  const editedReleaseFields = editedRelease?.fields ?? {};

  // True when a top-level scalar field is present (non-undefined) in either
  // response. We can't use `key in obj` because the OpenAPI generator
  // populates every optional property with `undefined` when missing, which
  // makes `'duration' in fields` true even when the BE didn't return it.
  const isFieldEdited = (key: string): boolean => {
    return (
      (editedRecordingFields as Record<string, unknown>)[key] != null ||
      (editedReleaseFields as Record<string, unknown>)[key] != null
    );
  };

  // Collect values flagged with op="add" for a multi-value field on either
  // the recording or release response. We match chips by value (per user
  // direction: "we'll be able to match it on the value").
  const addedValuesFor = (
    field: 'recordingVersion' | 'isrcs' | 'releaseLabels' | 'catNumbers',
  ): Set<string> => {
    const out = new Set<string>();
    const fromRec = (editedRecordingFields as any)[field];
    const fromRel = (editedReleaseFields as any)[field];
    for (const src of [fromRec, fromRel]) {
      const items: Array<{ op?: string; value?: unknown }> = src?.items ?? [];
      for (const it of items) {
        if (it.op === 'add' && typeof it.value === 'string') out.add(it.value);
      }
    }
    return out;
  };

  // Artists added (op === "add") from the recording response. We track both
  // the artist_id and the stageName so the chip render path can match either —
  // some legacy rows in `row.mainArtistsRaw` are bare strings (no id), so an
  // id-only match would miss them.
  const addedArtistIds = (): Set<string> => {
    const out = new Set<string>();
    const items: Array<{ op?: string; artistId?: string }> =
      editedRecording?.fields?.artists?.items ?? [];
    for (const it of items) {
      if (it.op === 'add' && it.artistId) out.add(it.artistId);
    }
    return out;
  };
  const addedArtistNames = (): Set<string> => {
    const out = new Set<string>();
    const items: Array<{ op?: string; stageName?: string }> =
      editedRecording?.fields?.artists?.items ?? [];
    for (const it of items) {
      if (it.op === 'add' && it.stageName) out.add(it.stageName);
    }
    return out;
  };

  // Contribution roles that were either added or edited.
  const editedContributionRoles = (): Set<string> => {
    const out = new Set<string>();
    const items: Array<{ op?: string; role?: string }> =
      editedRecording?.fields?.contributions?.items ?? [];
    for (const it of items) {
      if ((it.op === 'add' || it.op === 'edit') && it.role) out.add(it.role);
    }
    return out;
  };

  // For an edited/added contribution role, return the notes + evidence URL
  // captured in the manual-changes overlay. ADD ops carry them top-level;
  // EDIT ops carry them under `changes.{notes,source_url}.value`.
  // Returns undefined fields when the overlay didn't supply that sub-field
  // (the caller falls back to the legacy `confidencePattern` JSON).
  const getContributionOverlayForRole = (
    role: string,
  ): { notes?: string; sourceUrl?: string } | null => {
    const items: Array<{
      op?: string;
      role?: string;
      notes?: string | null;
      sourceUrl?: string | null;
      changes?: {
        notes?: { value?: unknown };
        sourceUrl?: { value?: unknown };
      };
    }> = editedRecording?.fields?.contributions?.items ?? [];
    const match = items.find((it) => it.role === role);
    if (!match) return null;
    const out: { notes?: string; sourceUrl?: string } = {};
    if (match.op === 'add') {
      if (match.notes != null) out.notes = String(match.notes);
      if (match.sourceUrl != null) out.sourceUrl = String(match.sourceUrl);
    } else if (match.op === 'edit' && match.changes) {
      if (match.changes.notes && match.changes.notes.value != null)
        out.notes = String(match.changes.notes.value);
      if (match.changes.sourceUrl && match.changes.sourceUrl.value != null)
        out.sourceUrl = String(match.changes.sourceUrl.value);
    }
    return out;
  };

  // Memoise the predicate sets for the render path.
  const addedArtistIdSet = addedArtistIds();
  const addedArtistNameSet = addedArtistNames();
  const editedContributionRoleSet = editedContributionRoles();
  const addedVersionValues = addedValuesFor('recordingVersion');
  const addedIsrcValues = addedValuesFor('isrcs');
  const addedLabelValues = addedValuesFor('releaseLabels');
  const addedCatNumberValues = addedValuesFor('catNumbers');

  // Debounced artist lookup via API (contract 4.4.1)
  useEffect(() => {
    if (!isRecordingEditable || !userId || !row?.id) return;
    const q = artistSearchQuery.trim();
    // Only call the API when the user has actually typed something — no
    // empty / wildcard searches.
    if (!q) {
      setArtistLookupResults([]);
      setArtistLookupLoading(false);
      return;
    }
    setArtistLookupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId: row.id,
          searchTerm: q,
        });
        setArtistLookupResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
            members: (a.members ?? []).map((m) => ({
              id: m.id ?? '',
              stageName: m.stageName ?? '',
            })),
          }))
        );
      } catch {
        setArtistLookupResults([]);
      } finally {
        setArtistLookupLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [artistSearchQuery, isRecordingEditable, userId, row?.id, api]);

  // Debounced member lookup for group artist creation
  useEffect(() => {
    if (!showCreateArtist || !newArtistIsGroup || !userId || !row?.id) return;
    const q = newArtistMemberSearch.trim();
    if (!q) {
      setNewArtistMemberResults([]);
      return;
    }
    setNewArtistMemberSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId: row.id,
          searchTerm: q,
        });
        setNewArtistMemberResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
          }))
        );
      } catch {
        setNewArtistMemberResults([]);
      } finally {
        setNewArtistMemberSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [
    newArtistMemberSearch,
    showCreateArtist,
    newArtistIsGroup,
    userId,
    row?.id,
    api,
  ]);

  // Auto-populate stage name and members from source ID lookup (Discogs / MusicBrainz)
  useEffect(() => {
    if (!showCreateArtist || !newArtistSourceId.trim() || !userId || !row?.id) {
      setNewArtistSourceLookupError(null);
      return;
    }
    setNewArtistSourceLookupLoading(true);
    setNewArtistSourceLookupError(null);
    const timer = setTimeout(async () => {
      try {
        const sourceParam =
          newArtistSource === 'MB'
            ? LookupSourceArtistLookupSourceArtistResponseSourceEnum.Musicbrainz
            : LookupSourceArtistLookupSourceArtistResponseSourceEnum.Discogs;
        const data = await api.lookupSourceArtistLookupSourceArtistResponse({
          userId,
          parentRecordingId: row.id,
          source: sourceParam,
          sourceId: newArtistSourceId.trim(),
        });
        if (data.stageName) {
          setNewArtistAlias(data.stageName);
          setNewArtistIsGroup(data.isGroup ?? false);
          setNewArtistExists(data._exists ?? false);
          setNewArtistMembers(
            (data.members ?? []).map((m) => ({
              // If the member already exists in WMC, prefer the internal id;
              // otherwise keep the source-side id.
              id: m._exists && m.internalId ? m.internalId : m.id,
              stageName: m.stageName,
              exists: m._exists ?? false,
            }))
          );
          setNewArtistSourceLookupError(null);
        } else {
          setNewArtistSourceLookupError('Artist not found for this ID');
        }
      } catch {
        setNewArtistSourceLookupError('Lookup failed — check the Source ID');
      } finally {
        setNewArtistSourceLookupLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    newArtistSourceId,
    newArtistSource,
    showCreateArtist,
    userId,
    row?.id,
    api,
  ]);

  useEffect(() => {
    if (row) {
      setArtistsError(null);
      const normalizedYear = row.year
        ? row.year.length <= 4
          ? row.year
          : row.year.slice(0, 4)
        : '';
      const initialFormData = {
        release: row.release,
        year: normalizedYear,
        format: row.format,
        mediaType: row.mediaType,
        title: row.title,
        duration: row.duration,
        isLive: row.isLive,
        isFeatured: row.isFeatured,
      };
      setFormData(initialFormData);

      // Use original arrays from the Row to avoid lossy comma-split round-trips
      const initialLabels = row.labelsArray ?? [];
      const initialCatNumbers = row.catNumbersArray ?? [];
      const initialVersions = row.versionsArray ?? [];
      const initialIsrcs = row.isrcsArray ?? [];
      const initialArtists: ArtistEntry[] =
        row.mainArtistsRaw && row.mainArtistsRaw.length > 0
          ? row.mainArtistsRaw.map((item) => {
              if (typeof item === 'string') {
                return {
                  name: item,
                  isFeatured: false,
                  featuredStatus: 'nf' as FeaturedStatus,
                };
              }
              const obj = item as MainArtistItem;
              return {
                name: obj.stageName,
                id: obj.id,
                isFeatured: false,
                featuredStatus: 'nf' as FeaturedStatus,
                type: obj.type,
              };
            })
          : row.artist
          ? row.artist
              .split(',')
              .map((name) => ({
                name: name.trim(),
                isFeatured: false,
                featuredStatus: 'nf' as FeaturedStatus,
              }))
              .filter((a) => a.name)
          : [];
      const initialContributions: RecordingWestburyContributionsInner[] =
        row.contributions && row.contributions.length > 0
          ? row.contributions.map((c) => ({
              contribution: c.contribution,
              confidenceCategory: c.confidenceCategory ?? '1',
              confidencePattern: c.confidencePattern ?? '',
            }))
          : [];

      setLabels(initialLabels);
      setCatNumbers(initialCatNumbers);
      setVersions(initialVersions);
      setIsrcs(initialIsrcs);
      setArtists(initialArtists);
      setDemotedArtists([]);
      setContributions(initialContributions);
      setShowCreateArtist(false);
      setNewArtistAlias('');
      setNewArtistFeatured('fa');
      setArtistSearchQuery('');
      setArtistDropdownOpen(false);
      setContributionCategoryOpenIndex(null);
      setCategoryDropdownRect(null);
      setShowContributionModal(false);
      setContributionModalContext(null);
      setContributionSearchQuery('');
      // Reset inline add-item inputs
      setIsrcInputOpen(false);
      setIsrcInputValue('');
      setLabelInputOpen(false);
      setLabelInputValue('');
      setCatNoInputOpen(false);
      setCatNoInputValue('');

      // Store original values for revert functionality
      setOriginalValues({
        formData: initialFormData,
        labels: initialLabels,
        catNumbers: initialCatNumbers,
        versions: initialVersions,
        isrcs: initialIsrcs,
        artists: initialArtists,
        contributions: initialContributions,
      });

      // Reset editing state when row changes
      setIsReleaseEditable(false);
      setRecordingSaveError(null);
      setReleaseSaveError(null);
      setYearError(null);
      setLabelsError(null);
      setCatNumbersError(null);
      setVersionsError(null);
    }
  }, [row]);

  const saveHandlerRef = useRef<() => void>(() => {});
  // Kept in sync each render (see assignment near the save handler ref) so the
  // imperative `isDirty()` reflects the latest recording/release dirty state.
  const isDirtyRef = useRef(false);
  useImperativeHandle(
    ref,
    () => ({
      save: () => saveHandlerRef.current?.(),
      cancel: onCancel,
      isDirty: () => isDirtyRef.current,
    }),
    [onCancel]
  );

  if (!row || !originalValues) return null;

  // Helper function to check if a field has been modified
  const isFieldModified = (field: string): boolean => {
    if (!originalValues) return false;

    switch (field) {
      case 'release':
        return formData.release !== originalValues.formData.release;
      case 'year':
        return formData.year !== originalValues.formData.year;
      case 'label':
        return JSON.stringify(labels) !== JSON.stringify(originalValues.labels);
      case 'catNumbers':
        return (
          JSON.stringify(catNumbers) !==
          JSON.stringify(originalValues.catNumbers)
        );
      case 'format':
        return formData.format !== originalValues.formData.format;
      case 'mediaType':
        return formData.mediaType !== originalValues.formData.mediaType;
      case 'artist':
        return (
          JSON.stringify(artists) !== JSON.stringify(originalValues.artists) ||
          demotedArtists.length > 0
        );
      case 'isFeatured':
        return false; // Featured is read-only, never considered modified
      case 'title':
        return formData.title !== originalValues.formData.title;
      case 'version':
        return (
          JSON.stringify(versions) !== JSON.stringify(originalValues.versions)
        );
      case 'isrcs':
        return JSON.stringify(isrcs) !== JSON.stringify(originalValues.isrcs);
      case 'duration':
        return formData.duration !== originalValues.formData.duration;
      case 'isLive':
        return formData.isLive !== originalValues.formData.isLive;
      case 'contributions':
        return (
          JSON.stringify(contributions) !==
          JSON.stringify(originalValues.contributions)
        );
      default:
        return false;
    }
  };

  // Check if any field in release section has been modified
  const isReleaseSectionModified = (): boolean => {
    return (
      isFieldModified('release') ||
      isFieldModified('year') ||
      isFieldModified('label') ||
      isFieldModified('catNumbers') ||
      isFieldModified('format') ||
      isFieldModified('mediaType')
    );
  };

  // Check if any field in recording section has been modified
  const isRecordingSectionModified = (): boolean => {
    return (
      isFieldModified('artist') ||
      isFieldModified('title') ||
      isFieldModified('version') ||
      isFieldModified('isrcs') ||
      isFieldModified('duration') ||
      isFieldModified('isLive') ||
      isFieldModified('isFeatured') ||
      isFieldModified('contributions')
    );
  };

  const handleReleaseLookup = async () => {
    if (!userId || !row?.id || !releaseId.trim()) {
      setReleaseLookupError(
        'User and recording context required. Enter a release ID.'
      );
      return;
    }
    if (releaseSource === 'Other') {
      setReleaseLookupError(
        'Use a catalog source (e.g. Discogs) to look up by ID.'
      );
      return;
    }
    const lookupSource = mapReleaseSourceToLookupEnum(releaseSource);
    if (!lookupSource) {
      setReleaseLookupError('Invalid source for lookup.');
      return;
    }
    // Check if the entered source ID matches the currently linked release
    const enteredId = releaseId.trim();
    const isSameRelease = (row.sourceReleaseId ?? []).some(
      (srcId) => srcId === enteredId || srcId.endsWith(`:${enteredId}`)
    );
    if (isSameRelease) {
      setReleaseSameAsCurrentError(true);
      return;
    }

    setReleaseSameAsCurrentError(false);
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setReleaseLookupLoading(true);
    try {
      const response = await api.lookupLookupReleaseResponse({
        userId,
        parentRecordingId: row.id,
        sourceId: releaseId.trim(),
        source: lookupSource,
      });
      const p = response.payload;
      setLookupReleaseId(p.releaseId);
      setLookupEdgeId(p.edgeId ?? null);
      setLookupDateReleased(p.dateReleased ?? null);
      if (p.releaseTitle) setNewReleaseTitle(p.releaseTitle);
      if (p.dateReleased) {
        const year =
          p.dateReleased.length >= 4
            ? p.dateReleased.slice(0, 4)
            : p.dateReleased;
        setNewReleaseYear(year);
        setNewReleaseYearError(validateReleaseYear(year));
      }
      if (p.releaseLabels?.length) setNewReleaseLabels([...p.releaseLabels]);
      if (p.catNumbers?.length) setNewReleaseCatNumbers([...p.catNumbers]);
      if (p.format) setNewReleaseFormat(p.format);
      if (p.mediaType) setNewReleaseType(p.mediaType);
      setReleaseLookupSuccess(true);
      if (!response.linked) {
        setReleaseLookupNotLinked(true);
      }
    } catch (err: any) {
      // Detect 404 (release not in system) — the generated client throws a
      // ResponseError whose response.status is 404.
      const status = err?.response?.status ?? err?.status;
      if (status === 404) {
        setReleaseLookupNotFound(true);
      } else {
        const message =
          err instanceof Error ? err.message : 'Release lookup failed';
        setReleaseLookupError(message);
      }
    } finally {
      setReleaseLookupLoading(false);
    }
  };

  const resetReleaseModalState = () => {
    setReleaseId('');
    setSourceUrl('');
    setReleaseSource('Discogs');
    setNewReleaseTitle('');
    setNewReleaseYear('');
    setNewReleaseLabels([]);
    setNewReleaseCatNumbers([]);
    setNewLabelInputOpen(false);
    setNewLabelInputValue('');
    setNewCatNoInputOpen(false);
    setNewCatNoInputValue('');
    setNewReleaseFormat('');
    setNewReleaseType('');
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setReleaseSameAsCurrentError(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setReleaseChangeSubmitError(null);
    setNewReleaseYearError(null);
  };

  const mergeReleaseIntoRow = (): Row => {
    if (!row) return {} as Row;
    return {
      ...row,
      ...formData,
      release: newReleaseTitle.trim(),
      year: newReleaseYear.trim() || '',
      label: newReleaseLabels.join(', '),
      catNumbers: newReleaseCatNumbers.join(', '),
      labelsArray: newReleaseLabels,
      catNumbersArray: newReleaseCatNumbers,
      format: newReleaseFormat,
      mediaType: newReleaseType,
      earliestParentReleaseId: lookupReleaseId || row.earliestParentReleaseId,
      sourceReleaseId: releaseId.trim()
        ? [releaseId.trim()]
        : row.sourceReleaseId,
      isModified: true,
    };
  };

  const handleChangeReleaseSubmit = async () => {
    if (!userId || !row?.id) return;

    if (newLabelInputOpen || newCatNoInputOpen) {
      setReleaseChangeSubmitError(
        `You have a ${
          newLabelInputOpen ? 'label' : 'catalogue number'
        } being entered. Please click Create to add it, or Cancel, before saving.`
      );
      return;
    }

    const dupNewLabel = findDuplicate(newReleaseLabels);
    if (dupNewLabel) {
      setReleaseChangeSubmitError(`Duplicate label: "${dupNewLabel}"`);
      return;
    }
    const dupNewCatNum = findDuplicate(newReleaseCatNumbers);
    if (dupNewCatNum) {
      setReleaseChangeSubmitError(
        `Duplicate catalogue number: "${dupNewCatNum}"`
      );
      return;
    }

    const isCreateAndLinkFlow =
      releaseSource === 'Other' || releaseLookupNotFound;
    setReleaseChangeSubmitError(null);
    setReleaseChangeSubmitLoading(true);
    try {
      if (isCreateAndLinkFlow) {
        const createRequest = {
          source: mapReleaseSourceToCreateEnum(releaseSource),
          releaseTitle: newReleaseTitle.trim(),
          dateReleased: newReleaseYear.trim() || undefined,
          releaseLabels:
            newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
          catNumbers:
            newReleaseCatNumbers.length > 0 ? newReleaseCatNumbers : undefined,
          format: newReleaseFormat || undefined,
          mediaType: newReleaseType || undefined,
          ...(releaseSource === 'Other'
            ? { sourceUrl: sourceUrl.trim() }
            : { sourceId: releaseId.trim() }),
        };
        await api.createAndLinkServiceResponse({
          userId,
          parentRecordingId: row.id,
          createReleaseAndLinkRequest: {
            ...createRequest,
            documentId: row.documentId,
          },
        });
      } else {
        if (!lookupReleaseId) {
          setReleaseChangeSubmitError('Missing release ID from lookup.');
          return;
        }
        await api.linkEarliestServiceResponse({
          userId,
          parentRecordingId: row.id,
          linkReleaseRequest: {
            releaseId: lookupReleaseId,
            edgeId: lookupEdgeId ?? undefined,
            dateReleased: lookupDateReleased ?? undefined,
            releaseTitle: newReleaseTitle.trim(),
            releaseLabels:
              newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
            catNumbers:
              newReleaseCatNumbers.length > 0
                ? newReleaseCatNumbers
                : undefined,
            mediaType: newReleaseType || undefined,
            format: newReleaseFormat || undefined,
            documentId: row.documentId,
          },
        });
      }
      const updatedRow = mergeReleaseIntoRow();
      setFormData((prev) => ({
        ...prev,
        release: newReleaseTitle.trim(),
        year: newReleaseYear.trim() || '',
        format: newReleaseFormat,
        mediaType: newReleaseType,
      }));
      setLabels(newReleaseLabels);
      setCatNumbers(newReleaseCatNumbers);
      onSave(updatedRow);
      setEditsVersion((v) => v + 1);
      resetReleaseModalState();
      setShowChangeReleaseModal(false);
    } catch (err) {
      setReleaseChangeSubmitError(
        await friendlySaveError(err, 'release-link')
      );
    } finally {
      setReleaseChangeSubmitLoading(false);
    }
  };

  const handleRevert = (type: 'section' | 'field', field?: string) => {
    if (type === 'section') {
      // Revert entire section
      if (field === 'release') {
        setFormData((prev) => ({
          ...prev,
          release: originalValues.formData.release,
          year: originalValues.formData.year,
          format: originalValues.formData.format,
          mediaType: originalValues.formData.mediaType,
        }));
        setLabels(originalValues.labels);
        setCatNumbers(originalValues.catNumbers);
      } else if (field === 'recording') {
        setFormData((prev) => ({
          ...prev,
          title: originalValues.formData.title,
          duration: originalValues.formData.duration,
          isLive: originalValues.formData.isLive,
          isFeatured: originalValues.formData.isFeatured,
        }));
        setVersions(originalValues.versions);
        setIsrcs(originalValues.isrcs);
        setArtists(originalValues.artists);
        setContributions(originalValues.contributions);
        setContributionCategoryOpenIndex(null);
        setCategoryDropdownRect(null);
      }
    } else if (type === 'field' && field) {
      // Revert specific field
      switch (field) {
        case 'release':
          handleFieldChange('release', originalValues.formData.release || '');
          break;
        case 'year':
          handleFieldChange('year', originalValues.formData.year || '');
          break;
        case 'label':
          setLabels(originalValues.labels);
          break;
        case 'catNumbers':
          setCatNumbers(originalValues.catNumbers);
          break;
        case 'format':
          handleFieldChange('format', originalValues.formData.format || '');
          break;
        case 'mediaType':
          handleFieldChange(
            'mediaType',
            originalValues.formData.mediaType || ''
          );
          break;
        case 'artist':
          setArtists(originalValues.artists);
          setDemotedArtists([]);
          break;
        case 'title':
          handleFieldChange('title', originalValues.formData.title || '');
          break;
        case 'version':
          setVersions(originalValues.versions);
          break;
        case 'isrcs':
          setIsrcs(originalValues.isrcs);
          break;
        case 'duration':
          handleFieldChange('duration', originalValues.formData.duration || '');
          break;
        case 'isLive':
          handleFieldChange('isLive', originalValues.formData.isLive || false);
          break;
        case 'isFeatured':
          handleFieldChange(
            'isFeatured',
            originalValues.formData.isFeatured || false
          );
          break;
        case 'contributions':
          setContributions(originalValues.contributions);
          setContributionCategoryOpenIndex(null);
          setCategoryDropdownRect(null);
          break;
      }
    }
    setShowRevertModal(false);
    setRevertTarget(null);
  };

  const handleReleaseCancel = () => {
    handleRevert('section', 'release');
    setReleaseSaveError(null);
    setYearError(null);
    setLabelsError(null);
    setCatNumbersError(null);
    setLabelInputOpen(false);
    setLabelInputValue('');
    setCatNoInputOpen(false);
    setCatNoInputValue('');
    onCancel();
  };

  const handleSaveReleaseDetails = async () => {
    if (!userId || !row?.id || !row.earliestParentReleaseId) return;

    if (labelInputOpen || catNoInputOpen) {
      setUnsavedItemWarning(labelInputOpen ? 'label' : 'catalogue number');
      return;
    }

    const dupLabel = findDuplicate(labels);
    if (dupLabel) {
      setLabelsError(`Duplicate label: "${dupLabel}"`);
      return;
    }
    setLabelsError(null);

    const dupCatNum = findDuplicate(catNumbers);
    if (dupCatNum) {
      setCatNumbersError(`Duplicate catalogue number: "${dupCatNum}"`);
      return;
    }
    setCatNumbersError(null);

    setReleaseSaveError(null);
    setReleaseSaveLoading(true);
    try {
      const edits: Array<{
        op: CorrectReleaseRequestEditsInnerOpEnum;
        field: string;
        value: string;
      }> = [];
      if (isFieldModified('release')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'release_title',
          value: formData.release ?? '',
        });
      }
      if (isFieldModified('year')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'date_released',
          value: formData.year ?? '',
        });
      }
      if (isFieldModified('label')) {
        const origLabels = originalValues?.labels ?? [];
        const addedLabels = labels.filter((v) => !origLabels.includes(v));
        const removedLabels = origLabels.filter((v) => !labels.includes(v));
        addedLabels.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Add,
            field: 'release_labels',
            value: v,
          })
        );
        removedLabels.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Delete,
            field: 'release_labels',
            value: v,
          })
        );
      }
      if (isFieldModified('catNumbers')) {
        const origCatNumbers = originalValues?.catNumbers ?? [];
        const addedCatNumbers = catNumbers.filter(
          (v) => !origCatNumbers.includes(v)
        );
        const removedCatNumbers = origCatNumbers.filter(
          (v) => !catNumbers.includes(v)
        );
        addedCatNumbers.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Add,
            field: 'cat_numbers',
            value: v,
          })
        );
        removedCatNumbers.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Delete,
            field: 'cat_numbers',
            value: v,
          })
        );
      }
      if (isFieldModified('format')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'format',
          value: formData.format ?? '',
        });
      }
      if (isFieldModified('mediaType')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'media_type',
          value: formData.mediaType ?? '',
        });
      }
      if (edits.length > 0) {
        await api.correctServiceResponse({
          userId,
          parentRecordingId: row.id,
          correctReleaseRequest: {
            releaseId: row.earliestParentReleaseId,
            edits,
            documentId: row.documentId,
          },
        });
      }
      // Propagate updated values back to the parent/grid
      const updatedRow: Row = {
        ...row,
        ...formData,
        label: labels.join(', '),
        catNumbers: catNumbers.join(', '),
        labelsArray: labels,
        catNumbersArray: catNumbers,
        isModified: true,
      };
      onSave(updatedRow);
      setEditsVersion((v) => v + 1);
      // Only leave edit mode on success — on failure keep the section editable
      // so the user can see the error above the Save CTA and retry.
      setIsReleaseEditable(false);
    } catch (err) {
      setReleaseSaveError(await friendlySaveError(err, 'release'));
    } finally {
      setReleaseSaveLoading(false);
    }
  };

  // ── Recording section handlers ──

  const handleRecordingCancel = () => {
    handleRevert('section', 'recording');
    setRecordingSaveError(null);
    setShowContributionModal(false);
    setContributionModalContext(null);
    setShowRemoveContributionModal(null);
    setArtistActionModalIndex(null);
    setShowCreateArtist(false);
    setNewArtistAlias('');
    setNewArtistFeatured('fa');
    setNewArtistSource('discogs');
    setNewArtistSourceId('');
    setNewArtistIsGroup(false);
    setNewArtistMembers([]);
    setNewArtistExists(false);
    setNewArtistMemberSearch('');
    setNewArtistSourceLookupError(null);
    setPendingArtistName(null);
    setPendingArtistId(null);
    setPendingArtistFeatured('fa');
    setEditingVersionIndex(null);
    setVersionsError(null);
    setArtistDropdownOpen(false);
    setAddContributionDropdownOpen(false);
    setContributionSearchQuery('');
    setArtistSearchQuery('');
    setIsrcFormatError(null);
    setPendingDuplicateIsrc(null);
    setIsrcInputOpen(false);
    setIsrcInputValue('');
  };

  const handleSaveRecordingDetails = async () => {
    if (isrcInputOpen) {
      setUnsavedItemWarning('ISRC');
      return;
    }
    if (!formData.title?.trim()) {
      setTitleError('Recording title is required');
      return;
    }
    setTitleError(null);

    const validArtists = artists.filter((a) => a.name.trim());
    if (validArtists.length === 0) {
      setArtistsError('At least one artist is required');
      return;
    }
    setArtistsError(null);

    const dupVersion = findDuplicate(versions);
    if (dupVersion) {
      setVersionsError(`Duplicate version: "${dupVersion}"`);
      return;
    }
    setVersionsError(null);

    const durationWasAlreadySet = !!originalValues?.formData?.duration
      ?.toString()
      .trim();
    const durationIsEditable =
      !durationWasAlreadySet || isFieldEdited('duration');
    if (
      durationIsEditable &&
      formData.duration &&
      !isValidDuration(formData.duration)
    ) {
      alert('Duration must be in HH:MM:SS format (e.g. 00:03:45)');
      return;
    }

    setRecordingSaveError(null);
    setRecordingSaveLoading(true);
    try {
      // ── 1. Correct recording data (contract 4.1.1) ──
      if (userId && row?.id) {
        const recordingEdits: CorrectRecordingRequestEditsInner[] = [];
        if (isFieldModified('title')) {
          recordingEdits.push({
            op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
            field: 'recordingTitle',
            value: (formData.title ?? '') as unknown as object,
          });
        }
        if (isFieldModified('duration')) {
          recordingEdits.push({
            op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
            field: 'duration',
            value: (formData.duration ?? '') as unknown as object,
          });
        }
        if (isFieldModified('isLive')) {
          // is_live: boolean flag (OS schema: ParentRecording.isLive)
          recordingEdits.push({
            op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
            field: 'is_live',
            value: !!formData.isLive as unknown as object,
          });
          // live: str — the user-provided live info from the modal prompt
          // (OS schema: ParentRecording.live, e.g. "Live in London, 1989")
          if (formData.isLive) {
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
              field: 'live',
              value: liveToYesNote.trim() as unknown as object,
            });
          } else {
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
              field: 'live',
              value: '' as unknown as object,
            });
          }
        }
        if (isFieldModified('version')) {
          // recordingVersion is list[str] in OS schema.
          // Send delete for removed versions and add for new ones.
          const origVersions = originalValues?.versions ?? [];
          const addedVersions = versions.filter(
            (v) => !origVersions.includes(v)
          );
          const removedVersions = origVersions.filter(
            (v) => !versions.includes(v)
          );
          removedVersions.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
              field: 'recordingVersion',
              value: v as unknown as object,
            })
          );
          addedVersions.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Add,
              field: 'recordingVersion',
              value: v as unknown as object,
            })
          );
        }
        if (isFieldModified('isrcs')) {
          const origIsrcs = originalValues?.isrcs ?? [];
          const added = isrcs.filter((v) => !origIsrcs.includes(v));
          const removed = origIsrcs.filter((v) => !isrcs.includes(v));
          added.forEach((v) => {
            const dupMatches = confirmedDuplicateIsrcs[v];
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Add,
              field: 'isrcs',
              value: v as unknown as object,
              dupIsrcs:
                dupMatches && dupMatches.length > 0
                  ? dupMatches.map((d) => ({
                      dupRecordingId: d.recordingId,
                      dupDocumentId: d.documentId,
                    }))
                  : undefined,
            });
          });
          removed.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
              field: 'isrcs',
              value: v as unknown as object,
            })
          );
        }

        if (recordingEdits.length > 0) {
          await api.correctRecordingServiceResponse({
            userId,
            parentRecordingId: row.id,
            correctRecordingRequest: {
              edits: recordingEdits,
              documentId: row.documentId,
            },
          });
        }

        // ── 2a. Create new artists and link (contract 4.4.2) ──
        // Pending-create artists are linked via /create in a single batched call,
        // so we don't include them in the /artists payload below.
        if (isFieldModified('artist')) {
          const origNames = (originalValues?.artists ?? []).map((a) => a.name);
          const pendingCreates = validArtists.filter(
            (a) => a.pendingCreate && !origNames.includes(a.name)
          );
          if (pendingCreates.length > 0) {
            const createEdits: CreateArtistAndLinkRequestEditsInner[] =
              pendingCreates.map((a) => ({
                op: CreateArtistAndLinkRequestEditsInnerOpEnum.Add,
                stageName: a.name,
                source: a.pendingCreate!
                  .source as CreateArtistAndLinkRequestEditsInnerSourceEnum,
                sourceId: a.pendingCreate!.sourceId,
                featuredStatus:
                  a.featuredStatus === 'fa'
                    ? CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum.Fa
                    : CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum.Ofa,
                members:
                  a.members && a.members.length > 0
                    ? a.members.map((m) => ({
                        id: m.id,
                        stageName: m.stageName,
                        _exists: m.exists ?? false,
                      }))
                    : undefined,
              }));
            await api.createArtistAndLinkServiceResponse({
              userId,
              parentRecordingId: row.id,
              createArtistAndLinkRequest: {
                edits: createEdits,
                documentId: row.documentId,
              },
            });
          }
        }

        // ── 2b. Change associated artists (contract 4.1.3) ──
        if (isFieldModified('artist')) {
          const origNames = (originalValues?.artists ?? []).map((a) => a.name);
          const currNames = validArtists.map((a) => a.name);
          const artistEdits: ChangeAssociatedArtistsRequestEditsInner[] = [];

          // Added artists — exclude pending-create artists (handled by /create above)
          validArtists
            .filter((a) => !origNames.includes(a.name) && !a.pendingCreate)
            .forEach((a) => {
              artistEdits.push({
                artistId: a.id || a.name,
                op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Add,
                featuredStatus: (a.featuredStatus ??
                  (a.isFeatured ? 'fa' : 'ofa')) as 'fa' | 'ofa' | 'nfa',
                stageName: a.name,
                members: a.members ?? [],
              });
            });

          // Removed artists — no longer in the artists array and not demoted
          const demotedNames = demotedArtists.map((d) => d.name);
          (originalValues?.artists ?? [])
            .filter(
              (a) =>
                !currNames.includes(a.name) && !demotedNames.includes(a.name)
            )
            .forEach((a) => {
              artistEdits.push({
                artistId: a.id || a.name,
                op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
                // @ts-expect-error -- contract allows null for remove
                featuredStatus: null,
                type: a.type,
              });
            });

          // Demoted artists — tracked separately, sent with nfa
          demotedArtists.forEach((a) => {
            artistEdits.push({
              artistId: a.id || a.name,
              op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
              featuredStatus: 'nfa' as any,
              type: a.type,
            });
          });

          if (artistEdits.length > 0) {
            await api.changeArtistsServiceResponse({
              userId,
              parentRecordingId: row.id,
              changeAssociatedArtistsRequest: {
                edits: artistEdits,
                documentId: row.documentId,
              },
            });
          }
        }

        // ── 3. Manage contributions (contract 4.2.1) ──
        if (isFieldModified('contributions')) {
          const origContribs = originalValues?.contributions ?? [];
          const contribEdits: ManageContributionsRequestEditsInner[] = [];

          // Added contributions
          contributions
            .filter(
              (c) =>
                !origContribs.some((oc) => oc.contribution === c.contribution)
            )
            .forEach((c) => {
              const { evidenceUrl, notes } = parseContributionEvidenceNotes(
                c.confidencePattern ?? ''
              );
              contribEdits.push({
                op: ManageContributionsRequestEditsInnerOpEnum.Add,
                role: c.contribution ?? '',
                personaId: (c as any).personaId || undefined,
                dataCategory: c.confidenceCategory ?? '1',
                sourceUrl: evidenceUrl || undefined,
                notes: notes || undefined,
              });
            });

          // Removed contributions
          origContribs
            .filter(
              (oc) =>
                !contributions.some((c) => c.contribution === oc.contribution)
            )
            .forEach((oc) => {
              contribEdits.push({
                op: ManageContributionsRequestEditsInnerOpEnum.Delete,
                role: oc.contribution ?? '',
              });
            });

          // Edited contributions (same role, changed category/pattern/persona)
          contributions
            .filter((c) => {
              const orig = origContribs.find(
                (oc) => oc.contribution === c.contribution
              );
              return (
                orig &&
                (orig.confidenceCategory !== c.confidenceCategory ||
                  orig.confidencePattern !== c.confidencePattern ||
                  (orig as any).personaId !== (c as any).personaId)
              );
            })
            .forEach((c) => {
              const { evidenceUrl, notes } = parseContributionEvidenceNotes(
                c.confidencePattern ?? ''
              );
              contribEdits.push({
                op: ManageContributionsRequestEditsInnerOpEnum.Edit,
                role: c.contribution ?? '',
                personaId: (c as any).personaId || undefined,
                dataCategory: c.confidenceCategory ?? '1',
                sourceUrl: evidenceUrl || undefined,
                notes: notes || undefined,
              });
            });

          if (contribEdits.length > 0) {
            await api.manageContributionsServiceResponse({
              userId,
              parentRecordingId: row.id,
              manageContributionsRequest: {
                edits: contribEdits,
                documentId: row.documentId,
              },
            });
          }
        }
      }

      // Build optimistic contributions for the table display.
      // - Unchanged contributions keep their original confidencePattern (tooltip).
      // - Edited or newly added contributions get "manual_update" as the pattern.
      // Sort by confidenceCategory descending (5→1) to match server ordering.
      const origContribMap = new Map(
        (originalValues?.contributions ?? []).map((c) => [c.contribution, c])
      );
      const optimisticContributions = contributions
        .map((c) => {
          const orig = origContribMap.get(c.contribution);
          const isNew = !orig;
          const isEdited =
            orig &&
            (orig.confidenceCategory !== c.confidenceCategory ||
              (orig as any).personaId !== (c as any).personaId);
          return {
            ...c,
            confidencePattern:
              isNew || isEdited
                ? 'manual_update'
                : orig?.confidencePattern ?? '',
          };
        })
        .sort((a, b) =>
          b.confidenceCategory.localeCompare(a.confidenceCategory)
        );

      // Project the local artist list back into the row's `mainArtistsRaw`
      // shape so reopening the modal (without a page refresh) shows the
      // updated artist set. Artists with a known UUID are emitted as the new
      // `MainArtistItem` form; pending-create artists (where the server hasn't
      // assigned a UUID yet) fall back to the legacy string form — they'll be
      // replaced with the canonical UUID on the next refresh.
      const optimisticMainArtists: (string | MainArtistItem)[] =
        validArtists.map((a) =>
          a.id
            ? {
                id: a.id,
                stageName: a.name,
                type: a.type ?? 'persona',
              }
            : a.name,
        );

      const updatedRow: Row = {
        ...row,
        ...formData,
        artist: validArtists.map((a) => a.name).join(', '),
        contributions: optimisticContributions,
        label: labels.join(', '),
        catNumbers: catNumbers.join(', '),
        version: versions.join(', '),
        isrcs: isrcs.join(', '),
        labelsArray: labels,
        catNumbersArray: catNumbers,
        versionsArray: versions,
        isrcsArray: isrcs,
        mainArtistsRaw: optimisticMainArtists,
        hasManualChanges: true,
        isModified: true,
      };
      // Build list of ISRCs reassigned from other recordings so the grid can
      // optimistically strip them from those rows.
      const reassignedIsrcs: ReassignedIsrc[] = Object.entries(
        confirmedDuplicateIsrcs
      ).map(([isrc, sources]) => ({
        isrc,
        fromRecordingIds: sources.map((s) => s.recordingId),
      }));

      onSave(
        updatedRow,
        reassignedIsrcs.length > 0 ? reassignedIsrcs : undefined
      );
      // Trigger a manual-changes overlay refetch so the just-saved edits show
      // up as purple indicators without requiring the user to close + reopen
      // the modal.
      setEditsVersion((v) => v + 1);
    } catch (err) {
      setRecordingSaveError(await friendlySaveError(err, 'recording'));
    } finally {
      setRecordingSaveLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper functions for managing multiple values
  const addMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    const trimmed = value.trim();
    if (trimmed) {
      setter((prev) =>
        prev.some((v) => v.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed]
      );
    }
  };

  const removeMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string
  ) => {
    setter((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  /** Validate ISRC uniqueness via the backend OpenSearch endpoint. */
  const validateIsrcDuplication = async (
    isrc: string
  ): Promise<{
    isDuplicate: boolean;
    matches: {
      recordingId: string;
      recordingTitle: string;
      artistName: string;
      documentId: string;
    }[];
  }> => {
    if (!userId || !row?.id) {
      return { isDuplicate: false, matches: [] };
    }
    try {
      const resp = await api.validateIsrcValidateIsrcResponse({
        userId,
        parentRecordingId: row.id,
        validateIsrcRequest: { isrc },
      });
      return {
        isDuplicate: resp.isDuplicate ?? false,
        matches: (resp.matches ?? []).map((m) => ({
          recordingId: m.recordingId ?? '',
          recordingTitle: m.recordingTitle ?? '',
          artistName: m.artistName ?? '',
          documentId: m.documentId ?? '',
        })),
      };
    } catch {
      // If the API call fails, allow the ISRC to be added (don't block on network errors)
      return { isDuplicate: false, matches: [] };
    }
  };

  /** Fetch client aliases (personas) for the Alias dropdown in the contribution modal. */
  const fetchClientAliases = async () => {
    if (!userId || !row?.id || clientAliases.length > 0) return;
    setClientAliasesLoading(true);
    try {
      const response = await api.lookupClientAliasesLookupClientAliasesResponse(
        {
          userId,
          parentRecordingId: row.id,
        }
      );
      setClientAliases(
        response.personas.map((p) => ({
          stageName: p.stageName,
          personaId: p.personaId,
        }))
      );
    } catch {
      // Silently fail — alias field will just be empty
    } finally {
      setClientAliasesLoading(false);
    }
  };

  /** Validate ISRC format: 2 letter country code, 3 alphanumeric registrant code,
   *  2 digit year, 5 digit designation code (12 chars total, e.g. GBAAA0912345). */
  const ISRC_REGEX = /^[A-Z]{2}[A-Z0-9]{3}\d{2}\d{5}$/;
  const validateIsrcFormat = (value: string): boolean => {
    return ISRC_REGEX.test(value.toUpperCase().replace(/[-\s]/g, ''));
  };

  const addIsrc = async (raw: string) => {
    if (!validateIsrcFormat(raw)) {
      setIsrcFormatError(
        'Invalid ISRC format. Expected: 2 letter country code, 3 char registrant code, 2 digit year, 5 digit designation (e.g. GBAAA0912345)'
      );
      return;
    }
    setIsrcFormatError(null);
    const { isDuplicate, matches } = await validateIsrcDuplication(raw);
    if (isDuplicate) {
      // Leave the inline input open behind the confirmation modal; it's
      // cleared/closed once the user confirms (see the Continue handler).
      setPendingDuplicateIsrc(raw);
      setPendingDuplicateMatches(matches);
      return;
    }
    addMultipleValue(setIsrcs, raw);
    setIsrcInputValue('');
    setIsrcInputOpen(false);
  };

  /** Commit the inline Label input into the labels list, then close it. */
  const commitLabel = () => {
    const trimmed = labelInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setLabels, trimmed);
    setLabelInputValue('');
    setLabelInputOpen(false);
  };

  /** Commit the inline Catalogue Number input into the list, then close it. */
  const commitCatNo = () => {
    const trimmed = catNoInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setCatNumbers, trimmed);
    setCatNoInputValue('');
    setCatNoInputOpen(false);
  };

  /** Commit helpers for the Create/Link-release modal's inline inputs. */
  const commitNewLabel = () => {
    const trimmed = newLabelInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setNewReleaseLabels, trimmed);
    setNewLabelInputValue('');
    setNewLabelInputOpen(false);
  };
  const commitNewCatNo = () => {
    const trimmed = newCatNoInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setNewReleaseCatNumbers, trimmed);
    setNewCatNoInputValue('');
    setNewCatNoInputOpen(false);
  };

  /** Validate duration is in HH:MM:SS format. */
  const isValidDuration = (value: string): boolean => {
    if (!value) return true; // empty is allowed (field is optional until filled)
    return /^\d{2}:\d{2}:\d{2}$/.test(value);
  };

  saveHandlerRef.current = handleSaveRecordingDetails;
  isDirtyRef.current = isRecordingSectionModified() || isReleaseSectionModified();

  return (
    <>
      <div
        className={
          stacked
            ? 'tw-flex tw-flex-col tw-gap-0 tw-mt-2 tw-pb-2'
            : 'tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-0 tw-mt-2 tw-pb-2 tw-overflow-y-auto tw-max-h-[calc(90vh-100px)]'
        }
      >
        {!releaseOnly && (
          <>
            {/* Recording section (includes Contributions, Artists, and Recording fields) */}
            <div
              className={
                stacked
                  ? 'tw-flex tw-flex-col tw-shrink-0'
                  : 'tw-flex tw-flex-col tw-overflow-y-auto'
              }
            >
              <div className="tw-px-3 tw-pt-2 tw-pb-2 tw-flex tw-flex-col tw-shrink-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
                  <div className="tw-text-white tw-font-semibold">
                    Recording
                  </div>
                </div>
                <div className="tw-space-y-4">
                  {/* ── Artists subsection ── */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <label
                        htmlFor="modal-artist-select"
                        className={`tw-text-sm tw-font-medium ${
                          isFieldModified('artist')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Artists
                      </label>
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {isRecordingEditable && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateArtist(true);
                            }}
                            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          >
                            <i className="wi wi-20px wi-plus tw-mr-1" />
                            <span className="tw-underline">Create artist</span>
                          </button>
                        )}
                      </div>
                    </div>
                    {artistsError && (
                      <p className="tw-text-westburyRed tw-text-xs tw-mb-1">
                        {artistsError}
                      </p>
                    )}
                    {artists.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        {artists.map((artist, index) => {
                          const origArtist = originalValues?.artists?.find(
                            (oa) => oa.name === artist.name
                          );
                          const isArtistAdded = !origArtist;
                          // Left-border indicator when this artist is in the
                          // manual-changes overlay (contract 4.5.2 artists.items)
                          // OR when the user just added them locally. We match
                          // on artist_id when available, falling back to
                          // stageName so legacy rows (where mainArtistsRaw is a
                          // plain string array) still light up correctly.
                          const showEditedBorder =
                            isArtistAdded ||
                            (!!artist.id && addedArtistIdSet.has(artist.id)) ||
                            addedArtistNameSet.has(artist.name);
                          return (
                            <ManualChangeChipWrap
                              key={index}
                              edited={showEditedBorder}
                            >
                              <div className="tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white">
                                <span>{artist.name}</span>
                                {isRecordingEditable && artists.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (artist.pendingCreate) {
                                        // Not yet created/linked — just remove locally
                                        setArtists((prev) =>
                                          prev.filter((_, i) => i !== index)
                                        );
                                      } else {
                                        setArtistActionModalIndex(index);
                                        setArtistActionConfirm(null);
                                      }
                                    }}
                                    className="tw-ml-0.5 tw-opacity-60 hover:tw-opacity-100 tw-flex tw-items-center"
                                    title={
                                      artist.pendingCreate
                                        ? 'Remove artist'
                                        : 'Demote or remove artist'
                                    }
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            </ManualChangeChipWrap>
                          );
                        })}
                      </div>
                    )}
                    <div className="tw-relative">
                      <input
                        id="modal-artist-select"
                        type="text"
                        value={artistSearchQuery}
                        onChange={(e) => {
                          setArtistSearchQuery(e.target.value);
                          setArtistDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (isRecordingEditable) setArtistDropdownOpen(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setArtistDropdownOpen(false), 200);
                        }}
                        placeholder="Search or select artist…"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                      />
                      {artistDropdownOpen && (
                        <div className="tw-absolute tw-z-50 tw-left-0 tw-right-0 tw-top-full tw-mt-0.5 tw-max-h-48 tw-overflow-y-auto tw-rounded tw-border tw-border-gray-600 tw-bg-gray-800 tw-py-1 tw-shadow-lg">
                          {!artistSearchQuery.trim() ? (
                            <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                              Start typing to search for artists…
                            </div>
                          ) : artistLookupLoading ? (
                            <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                              Searching...
                            </div>
                          ) : (
                            (() => {
                              const notAdded = artistLookupResults.filter(
                                (a) =>
                                  !artists.some(
                                    (e) =>
                                      e.name === a.stageName || e.id === a.id
                                  )
                              );
                              if (notAdded.length === 0) {
                                return (
                                  <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                                    {artistLookupResults.length === 0
                                      ? 'No matching artists'
                                      : 'All matching artists already added'}
                                  </div>
                                );
                              }
                              return notAdded.map((artist) => (
                                <button
                                  key={artist.id}
                                  type="button"
                                  className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setPendingArtistName(artist.stageName);
                                    setPendingArtistId(artist.id);
                                    setPendingArtistMembers(
                                      artist.members ?? []
                                    );
                                    setPendingArtistFeatured('fa');
                                    setArtistSearchQuery('');
                                    setArtistDropdownOpen(false);
                                  }}
                                >
                                  <span>{artist.stageName}</span>
                                  <span className="tw-text-gray-500 tw-text-xs tw-ml-2">
                                    {artist.id}
                                  </span>
                                </button>
                              ));
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recording Title */}
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <label
                        htmlFor="modal-title"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('title')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Recording Title
                      </label>
                    </div>
                    <ManualChangeWrap
                      edited={isFieldEdited('recordingTitle')}
                      changed={isFieldModified('title')}
                    >
                      <input
                        id="modal-title"
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => {
                          handleFieldChange('title', e.target.value);
                          if (titleError && e.target.value.trim())
                            setTitleError(null);
                        }}
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                          titleError ? 'tw-border-red-500' : 'tw-border-gray-600'
                        }`}
                      />
                    </ManualChangeWrap>
                    {titleError && (
                      <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                        {titleError}
                      </p>
                    )}
                  </div>

                  {/* Version (Multiple) */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-version-input"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('version')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Version
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    {versions.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                        {versions.map((version, index) => {
                          const isVersionEdited =
                            originalValues?.versions?.[index] !== undefined &&
                            originalValues.versions[index] !== version;
                          return editingVersionIndex === index ? (
                            <input
                              key={index}
                              type="text"
                              ref={(el) => el?.focus()}
                              value={version}
                              onChange={(e) =>
                                updateMultipleValue(
                                  setVersions,
                                  index,
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                updateMultipleValue(
                                  setVersions,
                                  index,
                                  e.target.value.trim()
                                );
                                setEditingVersionIndex(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Escape') {
                                  updateMultipleValue(
                                    setVersions,
                                    index,
                                    e.currentTarget.value.trim()
                                  );
                                  setEditingVersionIndex(null);
                                }
                              }}
                              className="tw-bg-gray-700 tw-border tw-border-westburyBlue tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-white tw-outline-none tw-min-w-[60px] tw-max-w-[160px]"
                            />
                          ) : (
                            <ManualChangeChipWrap
                              key={index}
                              edited={addedVersionValues.has(version)}
                            >
                              <div
                                role={
                                  isRecordingEditable ? 'button' : undefined
                                }
                                tabIndex={isRecordingEditable ? 0 : undefined}
                                className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white tw-border-2 tw-border-transparent ${
                                  isRecordingEditable
                                    ? 'tw-cursor-pointer hover:tw-border-white/50'
                                    : 'tw-opacity-60'
                                }`}
                                title={
                                  addedVersionValues.has(version)
                                    ? undefined
                                    : isRecordingEditable
                                    ? 'Click to edit'
                                    : undefined
                                }
                                onClick={() => {
                                  if (isRecordingEditable)
                                    setEditingVersionIndex(index);
                                }}
                                onKeyDown={(e) => {
                                  if (
                                    isRecordingEditable &&
                                    (e.key === 'Enter' || e.key === ' ')
                                  ) {
                                    e.preventDefault();
                                    setEditingVersionIndex(index);
                                  }
                                }}
                              >
                                {isVersionEdited && (
                                  <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                                )}
                                <span>{version}</span>
                              </div>
                            </ManualChangeChipWrap>
                          );
                        })}
                      </div>
                    )}
                    {versions.length === 0 && (
                      <p className="tw-text-gray-400 tw-text-xs">None</p>
                    )}
                    {versionsError && (
                      <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                        {versionsError}
                      </p>
                    )}
                  </div>

                  {/* ── Contributions subsection ── */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <label
                        htmlFor="modal-contribution-search"
                        className={`tw-text-sm tw-font-medium ${
                          isFieldModified('contributions')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Contributions
                      </label>
                    </div>
                    {contributions.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        {[...contributions]
                          .map((cont, originalIndex) => ({
                            cont,
                            originalIndex,
                          }))
                          .sort(
                            (a, b) =>
                              parseInt(a.cont.confidenceCategory ?? '1', 10) -
                              parseInt(b.cont.confidenceCategory ?? '1', 10)
                          )
                          .map(({ cont, originalIndex }) => {
                            const style = getContributionPillStyle(
                              cont.confidenceCategory ?? '1'
                            );
                            const origContrib =
                              originalValues?.contributions?.find(
                                (oc) => oc.contribution === cont.contribution
                              );
                            const isAdded = !origContrib;
                            const isEdited =
                              !!origContrib &&
                              (origContrib.confidenceCategory !==
                                cont.confidenceCategory ||
                                origContrib.confidencePattern !==
                                  cont.confidencePattern);
                            const contributionEdited =
                              editedContributionRoleSet.has(
                                cont.contribution ?? '',
                              );
                            return (
                              <ManualChangeChipWrap
                                key={originalIndex}
                                edited={contributionEdited}
                              >
                                <div
                                  className="tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-uppercase tw-border-2 tw-border-transparent tw-transition-colors tw-cursor-pointer hover:tw-border-white/50"
                                  style={style}
                                  title={
                                    contributionEdited
                                      ? undefined
                                      : isAdded
                                      ? 'Newly added contribution'
                                      : isEdited
                                      ? 'Edited contribution'
                                      : 'Click to edit confidence category'
                                  }
                                >
                                {(isAdded || isEdited) && (
                                  <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                                )}
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isRecordingEditable) return;
                                    const c = contributions[originalIndex];
                                    const parsed = parseContributionEvidenceNotes(
                                      c.confidencePattern ?? ''
                                    );
                                    const overlay = getContributionOverlayForRole(
                                      c.contribution ?? ''
                                    );
                                    setContributionModalForm({
                                      contributionName: c.contribution ?? '',
                                      confidenceCategory:
                                        c.confidenceCategory ?? '1',
                                      evidenceUrl:
                                        overlay?.sourceUrl ?? parsed.evidenceUrl,
                                      notes:
                                        overlay?.notes ?? parsed.notes,
                                      personaId: (c as any).personaId ?? '',
                                    });
                                    setContributionModalEvidenceError(null);
                                    setContributionModalContext({
                                      type: 'edit',
                                      index: originalIndex,
                                    });
                                    fetchClientAliases();
                                    setShowContributionModal(true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!isRecordingEditable) return;
                                      const c = contributions[originalIndex];
                                      const parsed = parseContributionEvidenceNotes(
                                        c.confidencePattern ?? ''
                                      );
                                      const overlay = getContributionOverlayForRole(
                                        c.contribution ?? ''
                                      );
                                      setContributionModalForm({
                                        contributionName: c.contribution ?? '',
                                        confidenceCategory:
                                          c.confidenceCategory ?? '1',
                                        evidenceUrl:
                                          overlay?.sourceUrl ??
                                          parsed.evidenceUrl,
                                        notes:
                                          overlay?.notes ?? parsed.notes,
                                        personaId: (c as any).personaId ?? '',
                                      });
                                      setContributionModalEvidenceError(null);
                                      setContributionModalContext({
                                        type: 'edit',
                                        index: originalIndex,
                                      });
                                      fetchClientAliases();
                                      setShowContributionModal(true);
                                    }
                                  }}
                                >
                                  {capitalize(cont.contribution)}
                                </span>
                                {isRecordingEditable &&
                                  contributions.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRemoveContributionModal(
                                          originalIndex
                                        );
                                      }}
                                      className="tw-ml-0.5 tw-opacity-60 hover:tw-opacity-100 tw-flex tw-items-center"
                                      title="Remove contribution"
                                    >
                                      <X size={12} />
                                    </button>
                                  )}
                                </div>
                              </ManualChangeChipWrap>
                            );
                          })}
                      </div>
                    )}
                    <div className="tw-relative">
                      <input
                        id="modal-contribution-search"
                        type="text"
                        value={contributionSearchQuery}
                        onChange={(e) => {
                          setContributionSearchQuery(e.target.value);
                          setAddContributionDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (isRecordingEditable)
                            setAddContributionDropdownOpen(true);
                        }}
                        onBlur={() => {
                          setTimeout(
                            () => setAddContributionDropdownOpen(false),
                            200
                          );
                        }}
                        placeholder="Search or select contribution…"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                      />
                      {addContributionDropdownOpen && (
                        <div className="tw-absolute tw-z-50 tw-left-0 tw-right-0 tw-top-full tw-mt-0.5 tw-max-h-48 tw-overflow-y-auto tw-overflow-x-hidden tw-rounded tw-border tw-border-gray-600 tw-bg-gray-800 tw-py-1 tw-shadow-lg">
                          {(() => {
                            const q = contributionSearchQuery
                              .trim()
                              .toLowerCase();
                            const filtered = availableContributions.filter(
                              (cont) =>
                                (!q || cont.toLowerCase().includes(q)) &&
                                !contributions.some(
                                  (c) => c.contribution === cont
                                )
                            );
                            if (filtered.length === 0) {
                              return (
                                <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                                  No matching contributions
                                </div>
                              );
                            }
                            return filtered.map((cont) => (
                              <button
                                key={cont}
                                type="button"
                                className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setContributionModalForm({
                                    contributionName: cont,
                                    confidenceCategory: '1',
                                    evidenceUrl: '',
                                    notes: '',
                                    personaId: '',
                                  });
                                  setContributionModalEvidenceError(null);
                                  setContributionModalContext({
                                    type: 'add',
                                    contributionValue: cont,
                                  });
                                  fetchClientAliases();
                                  setShowContributionModal(true);
                                  setAddContributionDropdownOpen(false);
                                  setContributionSearchQuery('');
                                }}
                              >
                                {capitalize(cont)}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ISRCs (Multiple) */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-isrcs-input"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('isrcs')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        ISRCs
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    {isrcs.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                        {isrcs.map((isrc, index) => {
                          const isIsrcAdded =
                            !originalValues?.isrcs?.includes(isrc);
                          const showEditedBorder = addedIsrcValues.has(isrc);
                          return (
                            <ManualChangeChipWrap
                              key={index}
                              edited={showEditedBorder}
                            >
                              <div
                                className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                                  !isRecordingEditable ? 'tw-opacity-60' : ''
                                }`}
                              >
                                {isIsrcAdded && (
                                  <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                                )}
                                <span>{isrc}</span>
                                {isRecordingEditable && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      removeMultipleValue(setIsrcs, index);
                                      if (isrcFormatError)
                                        setIsrcFormatError(null);
                                    }}
                                    className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                                    title="Remove"
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            </ManualChangeChipWrap>
                          );
                        })}
                      </div>
                    )}
                    {isrcs.length === 0 && (
                      <p className="tw-text-gray-400 tw-text-xs tw-mb-2">None</p>
                    )}
                    {isRecordingEditable && (
                      <div className="tw-flex tw-flex-col tw-gap-1.5">
                        {isrcInputOpen ? (
                          <>
                            <input
                              id="modal-isrcs-input"
                              type="text"
                              autoFocus
                              placeholder="Enter ISRC"
                              value={isrcInputValue}
                              onChange={(e) => {
                                setIsrcInputValue(e.target.value);
                                if (isrcFormatError) setIsrcFormatError(null);
                              }}
                              onKeyDown={(e) => {
                                if (
                                  e.key === 'Enter' &&
                                  isrcInputValue.trim()
                                ) {
                                  e.preventDefault();
                                  addIsrc(isrcInputValue.trim());
                                } else if (e.key === 'Escape') {
                                  setIsrcInputValue('');
                                  setIsrcInputOpen(false);
                                  setIsrcFormatError(null);
                                }
                              }}
                              className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                            />
                            <div className="tw-flex tw-justify-end tw-gap-2">
                              <WButton
                                variant="secondary"
                                label="Cancel"
                                onClick={() => {
                                  setIsrcInputValue('');
                                  setIsrcInputOpen(false);
                                  setIsrcFormatError(null);
                                }}
                              />
                              <WButton
                                variant="primary"
                                label="Create"
                                disabled={!isrcInputValue.trim()}
                                onClick={() => addIsrc(isrcInputValue.trim())}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="tw-flex tw-justify-end">
                            <button
                              type="button"
                              className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                              onClick={() => {
                                setIsrcInputValue('');
                                setIsrcFormatError(null);
                                setIsrcInputOpen(true);
                              }}
                            >
                              <i className="wi wi-20px wi-plus tw-mr-1" />
                              <span className="tw-underline">Add ISRC</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {isrcFormatError && (
                      <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                        {isrcFormatError}
                      </p>
                    )}
                  </div>

                  {/* Duration, Live, Featured — stacked single column */}
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <label
                          htmlFor="modal-duration"
                          className={`tw-block tw-text-sm tw-font-medium ${
                            isFieldModified('duration')
                              ? 'tw-text-white'
                              : originalValues?.formData?.duration &&
                                !isFieldEdited('duration')
                              ? 'tw-text-gray-400'
                              : 'tw-text-white'
                          }`}
                        >
                          Duration
                        </label>
                      </div>
                      <ManualChangeWrap
                        edited={isFieldEdited('duration')}
                        changed={isFieldModified('duration')}
                      >
                        <input
                          id="modal-duration"
                          type="text"
                          value={formData.duration || ''}
                          onChange={(e) =>
                            handleFieldChange('duration', e.target.value)
                          }
                          disabled={
                            !isRecordingEditable ||
                            (!!originalValues?.formData?.duration
                              ?.toString()
                              .trim() &&
                              !isFieldEdited('duration'))
                          }
                          className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                            (!originalValues?.formData?.duration
                              ?.toString()
                              .trim() ||
                              isFieldEdited('duration')) &&
                            formData.duration &&
                            !isValidDuration(formData.duration)
                              ? 'tw-border-red-500'
                              : 'tw-border-gray-600'
                          }`}
                          placeholder="HH:MM:SS"
                        />
                      </ManualChangeWrap>
                      {(!originalValues?.formData?.duration
                        ?.toString()
                        .trim() ||
                        isFieldEdited('duration')) &&
                        formData.duration &&
                        !isValidDuration(formData.duration) && (
                          <p className="tw-text-red-400 tw-text-xs tw-mt-0.5">
                            Format: HH:MM:SS (e.g. 00:03:45)
                          </p>
                        )}
                    </div>

                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <label
                          htmlFor="modal-live"
                          className={`tw-block tw-text-sm tw-font-medium ${
                            isFieldModified('isLive')
                              ? 'tw-text-white'
                              : 'tw-text-white'
                          }`}
                        >
                          Live
                        </label>
                      </div>
                      {(() => {
                        const liveField = (editedRecordingFields as any)
                          .live as { note?: string } | undefined;
                        const note = liveField?.note;
                        const liveEdited =
                          isFieldModified('isLive') || isFieldEdited('live');
                        const tooltip = note
                          ? `${MANUAL_CHANGE_TOOLTIP} — ${note}`
                          : MANUAL_CHANGE_TOOLTIP;
                        return (
                          <ManualChangeWrap
                            edited={liveEdited}
                            changed={isFieldModified('isLive')}
                            title={tooltip}
                          >
                            <select
                              id="modal-live"
                              value={formData.isLive ? 'Yes' : 'No'}
                              disabled={!isRecordingEditable}
                              onChange={(e) => {
                                const newLive = e.target.value === 'Yes';
                                if (!formData.isLive && newLive) {
                                  setLiveToYesNote('');
                                  setShowLiveToYesModal(true);
                                }
                              }}
                              className={`${SELECT_BASE_CLASS} ${selectTextClass(
                                true,
                              )}`}
                            >
                              <option value="No" disabled={formData.isLive}>
                                No
                              </option>
                              <option value="Yes">Yes</option>
                            </select>
                          </ManualChangeWrap>
                        );
                      })()}
                    </div>

                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <label
                          htmlFor="modal-featured"
                          className="tw-block tw-text-sm tw-font-medium tw-text-gray-400"
                        >
                          Featured
                        </label>
                      </div>
                      <select
                        id="modal-featured"
                        value={formData.isFeatured ? 'F' : 'NF'}
                        disabled
                        className={`${SELECT_BASE_CLASS} ${selectTextClass(
                          true,
                        )}`}
                      >
                        <option value="NF">Non-Featured (NF)</option>
                        <option value="F">Featured (F)</option>
                      </select>
                    </div>
                  </div>

                  {/* Cancel / Save — always shown (no separate "edit" step),
                      disabled until the recording has unsaved changes. */}
                  {recordingSaveError && (
                    <p className="tw-text-westburyRed tw-text-xs tw-mt-3">
                      {recordingSaveError}
                    </p>
                  )}
                  <div className="tw-flex tw-gap-2 tw-items-center tw-mt-3 tw-pt-4 tw-border-t tw-border-gray-600 tw-pb-3">
                    <WButton
                      variant="secondary"
                      label="Cancel"
                      onClick={handleRecordingCancel}
                      disabled={!isRecordingSectionModified()}
                    />
                    <WButton
                      variant="primary"
                      label={recordingSaveLoading ? 'Saving…' : 'Save Changes'}
                      onClick={handleSaveRecordingDetails}
                      disabled={
                        !isRecordingSectionModified() || recordingSaveLoading
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Separator */}
            <div
              className={
                stacked
                  ? 'tw-border-t tw-border-gray-600 tw-shrink-0'
                  : 'tw-border-l tw-border-gray-600'
              }
            />
          </>
        )}

        {/* Release Section */}
        <div
          className={
            stacked
              ? 'tw-p-3 tw-flex tw-flex-col tw-shrink-0'
              : 'tw-pr-4 tw-p-3 tw-flex tw-flex-col tw-overflow-y-auto'
          }
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
            <div className="tw-text-white tw-font-semibold">Release</div>
          </div>
          <div className="tw-space-y-4 tw-flex-1">
            {/* Release Title and Year of Release - stacked single column */}
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-release-title"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('release')
                        ? 'tw-text-white'
                        : 'tw-text-white'
                    }`}
                  >
                    Release Title
                  </label>
                </div>
                <ManualChangeWrap
                  edited={isFieldEdited('releaseTitle')}
                  changed={isFieldModified('release')}
                >
                  <input
                    id="modal-release-title"
                    type="text"
                    value={formData.release || ''}
                    onChange={(e) =>
                      handleFieldChange('release', e.target.value)
                    }
                    readOnly={!isReleaseEditable}
                    disabled={!isReleaseEditable}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                  />
                </ManualChangeWrap>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="modal-year"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('year')
                        ? 'tw-text-white'
                        : 'tw-text-white'
                    }`}
                  >
                    Year of Release
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                <ManualChangeWrap
                  edited={isFieldEdited('dateReleased')}
                  changed={isFieldModified('year')}
                >
                  <input
                    id="modal-year"
                    type="text"
                    value={formData.year ?? ''}
                    onChange={(e) => {
                      const yearValue = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 4);
                      setYearError(validateReleaseYear(yearValue));
                      handleFieldChange('year', yearValue);
                    }}
                    readOnly={!isReleaseEditable}
                    disabled={!isReleaseEditable}
                    className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                      yearError ? 'tw-border-red-500' : 'tw-border-gray-600'
                    }`}
                    placeholder="YYYY"
                    maxLength={4}
                  />
                </ManualChangeWrap>
                {yearError && (
                  <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                    {yearError}
                  </p>
                )}
              </div>
            </div>

            {/* Label (Multiple) */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <label
                  htmlFor="modal-label-input"
                  className={`tw-block tw-text-sm tw-font-medium ${
                    isFieldModified('label')
                      ? 'tw-text-white'
                      : 'tw-text-white'
                  }`}
                >
                  Label (Multiple)
                </label>
                <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                  Optional
                </span>
              </div>
              {labels.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                  {labels.map((label, index) => {
                    const isLabelAdded =
                      !originalValues?.labels?.includes(label);
                    return (
                    <ManualChangeChipWrap
                      key={index}
                      edited={addedLabelValues.has(label)}
                    >
                      <div
                        className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                          !isReleaseEditable ? 'tw-opacity-60' : ''
                        }`}
                      >
                        {isLabelAdded && (
                          <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                        )}
                        <span>{label}</span>
                        {isReleaseEditable && (
                          <button
                            type="button"
                            onClick={() =>
                              removeMultipleValue(setLabels, index)
                            }
                            className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </ManualChangeChipWrap>
                    );
                  })}
                </div>
              )}
              {labels.length === 0 && (
                <p className="tw-text-gray-400 tw-text-xs tw-mb-2">None</p>
              )}
              {isReleaseEditable && (
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  {labelInputOpen ? (
                    <>
                      <input
                        id="modal-label-input"
                        type="text"
                        autoFocus
                        placeholder="Enter label"
                        value={labelInputValue}
                        onChange={(e) => setLabelInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && labelInputValue.trim()) {
                            e.preventDefault();
                            commitLabel();
                          } else if (e.key === 'Escape') {
                            setLabelInputValue('');
                            setLabelInputOpen(false);
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      <div className="tw-flex tw-justify-end tw-gap-2">
                        <WButton
                          variant="secondary"
                          label="Cancel"
                          onClick={() => {
                            setLabelInputValue('');
                            setLabelInputOpen(false);
                          }}
                        />
                        <WButton
                          variant="primary"
                          label="Create"
                          disabled={!labelInputValue.trim()}
                          onClick={commitLabel}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="tw-flex tw-justify-end">
                      <button
                        type="button"
                        className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                        onClick={() => {
                          setLabelInputValue('');
                          setLabelInputOpen(true);
                        }}
                      >
                        <i className="wi wi-20px wi-plus tw-mr-1" />
                        <span className="tw-underline">Add label</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {labelsError && (
                <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                  {labelsError}
                </p>
              )}
            </div>

            {/* Cat no. (Multiple) */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <label
                  htmlFor="modal-catno-input"
                  className={`tw-block tw-text-sm tw-font-medium ${
                    isFieldModified('catNumbers')
                      ? 'tw-text-white'
                      : 'tw-text-white'
                  }`}
                >
                  Catalogue Number (Multiple)
                </label>
                <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                  Optional
                </span>
              </div>
              {catNumbers.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                  {catNumbers.map((catNo, index) => {
                    const isCatNoAdded =
                      !originalValues?.catNumbers?.includes(catNo);
                    return (
                    <ManualChangeChipWrap
                      key={index}
                      edited={addedCatNumberValues.has(catNo)}
                    >
                      <div
                        className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                          !isReleaseEditable ? 'tw-opacity-60' : ''
                        }`}
                      >
                        {isCatNoAdded && (
                          <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                        )}
                        <span>{catNo}</span>
                        {isReleaseEditable && (
                          <button
                            type="button"
                            onClick={() =>
                              removeMultipleValue(setCatNumbers, index)
                            }
                            className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </ManualChangeChipWrap>
                    );
                  })}
                </div>
              )}
              {catNumbers.length === 0 && (
                <p className="tw-text-gray-400 tw-text-xs tw-mb-2">None</p>
              )}
              {isReleaseEditable && (
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  {catNoInputOpen ? (
                    <>
                      <input
                        id="modal-catno-input"
                        type="text"
                        autoFocus
                        placeholder="Enter catalogue number"
                        value={catNoInputValue}
                        onChange={(e) => setCatNoInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && catNoInputValue.trim()) {
                            e.preventDefault();
                            commitCatNo();
                          } else if (e.key === 'Escape') {
                            setCatNoInputValue('');
                            setCatNoInputOpen(false);
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      <div className="tw-flex tw-justify-end tw-gap-2">
                        <WButton
                          variant="secondary"
                          label="Cancel"
                          onClick={() => {
                            setCatNoInputValue('');
                            setCatNoInputOpen(false);
                          }}
                        />
                        <WButton
                          variant="primary"
                          label="Create"
                          disabled={!catNoInputValue.trim()}
                          onClick={commitCatNo}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="tw-flex tw-justify-end">
                      <button
                        type="button"
                        className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                        onClick={() => {
                          setCatNoInputValue('');
                          setCatNoInputOpen(true);
                        }}
                      >
                        <i className="wi wi-20px wi-plus tw-mr-1" />
                        <span className="tw-underline">
                          Add catalogue number
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {catNumbersError && (
                <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                  {catNumbersError}
                </p>
              )}
            </div>

            {/* Format and Type - stacked single column */}
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="modal-format"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('format')
                        ? 'tw-text-white'
                        : 'tw-text-white'
                    }`}
                  >
                    Format
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                <ManualChangeWrap
                  edited={isFieldEdited('format')}
                  changed={isFieldModified('format')}
                >
                  <select
                    id="modal-format"
                    value={formData.format || ''}
                    onChange={(e) =>
                      handleFieldChange('format', e.target.value)
                    }
                    disabled={!isReleaseEditable}
                    className={`${SELECT_BASE_CLASS} ${selectTextClass(
                      !!formData.format,
                    )}`}
                  >
                    <option value="">Select Format</option>
                    {availableFormats.map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </ManualChangeWrap>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="modal-type"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('mediaType')
                        ? 'tw-text-white'
                        : 'tw-text-white'
                    }`}
                  >
                    Type
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                <ManualChangeWrap
                  edited={isFieldEdited('mediaType')}
                  changed={isFieldModified('mediaType')}
                >
                  <select
                    id="modal-type"
                    value={formData.mediaType || ''}
                    onChange={(e) =>
                      handleFieldChange('mediaType', e.target.value)
                    }
                    disabled={!isReleaseEditable}
                    className={`${SELECT_BASE_CLASS} ${selectTextClass(
                      !!formData.mediaType,
                    )}`}
                  >
                    <option value="">Select Type</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </ManualChangeWrap>
              </div>
            </div>

            {releaseSaveError && (
              <p className="tw-text-westburyRed tw-text-xs tw-mt-3">
                {releaseSaveError}
              </p>
            )}
            {/* Release Actions */}
            <div className="tw-flex tw-gap-2 tw-items-center tw-mt-3 tw-pt-4 tw-border-t tw-border-gray-600">
              {!isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Edit Release Details"
                    onClick={() => setShowReleaseCorrectionModal(true)}
                  />
                  <WButton
                    variant="secondary"
                    label="Change Linked Release"
                    onClick={() => setShowChangeReleaseModal(true)}
                  />
                </>
              )}
              {isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Cancel"
                    onClick={handleReleaseCancel}
                  />
                  <WButton
                    variant="primary"
                    label={
                      releaseSaveLoading ? 'Saving…' : 'Save Release Details'
                    }
                    onClick={handleSaveReleaseDetails}
                    disabled={
                      releaseSaveLoading ||
                      !formData.release?.trim() ||
                      !!yearError
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Release Correction Confirmation Modal */}
      <WModal
        isOpen={showReleaseCorrectionModal}
        onClose={() => setShowReleaseCorrectionModal(false)}
        title="Confirm Release Correction"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You are about to correct release data that is linked to multiple
            recordings. Do you wish to proceed?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowReleaseCorrectionModal(false)}
            />
            <WButton
              variant="primary"
              label="Proceed"
              onClick={() => {
                setShowReleaseCorrectionModal(false);
                setIsReleaseEditable(true);
                // In a real implementation, this would trigger the release correction logic
                // For now, it's just a mock, so we enable editing
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Contribution Confidence modal */}
      <WModal
        isOpen={showContributionModal}
        onClose={() => {
          setShowContributionModal(false);
          setContributionModalContext(null);
          setContributionModalEvidenceError(null);
        }}
        title={`Contribution`}
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
              Contribution
            </span>
            <p className="tw-text-white tw-text-sm">
              {contributionModalForm.contributionName
                ? capitalize(contributionModalForm.contributionName)
                : '—'}
            </p>
          </div>
          <div>
            <label
              htmlFor="contribution-modal-alias"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Alias
            </label>
            <select
              id="contribution-modal-alias"
              value={contributionModalForm.personaId}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  personaId: e.target.value,
                }))
              }
              disabled={clientAliasesLoading}
              className={`${SELECT_BASE_CLASS} ${selectTextClass(
                !!contributionModalForm.personaId,
              )}`}
            >
              <option value="">
                {clientAliasesLoading ? 'Loading aliases…' : 'Select alias'}
              </option>
              {clientAliases.map((alias) => (
                <option key={alias.personaId} value={alias.personaId}>
                  {alias.stageName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="contribution-modal-confidence"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Data Quality Category
            </label>
            <select
              id="contribution-modal-confidence"
              value={contributionModalForm.confidenceCategory}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  confidenceCategory: e.target.value,
                }))
              }
              className={`${SELECT_BASE_CLASS} ${selectTextClass(
                !!contributionModalForm.confidenceCategory,
              )}`}
            >
              {CONFIDENCE_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <label
                htmlFor="contribution-modal-evidence"
                className="tw-block tw-text-white tw-text-sm tw-font-medium"
              >
                Evidence URL
              </label>
              <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                Optional
              </span>
            </div>
            <input
              id="contribution-modal-evidence"
              type="url"
              value={contributionModalForm.evidenceUrl}
              onChange={(e) => {
                setContributionModalForm((prev) => ({
                  ...prev,
                  evidenceUrl: e.target.value,
                }));
                if (contributionModalEvidenceError)
                  setContributionModalEvidenceError(null);
              }}
              placeholder="https://..."
              className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                contributionModalEvidenceError
                  ? 'tw-border-red-500'
                  : 'tw-border-gray-600'
              }`}
            />
            {contributionModalEvidenceError && (
              <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                {contributionModalEvidenceError}
              </p>
            )}
          </div>
          <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <label
                htmlFor="contribution-modal-notes"
                className="tw-block tw-text-white tw-text-sm tw-font-medium"
              >
                Notes
              </label>
              <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                Optional
              </span>
            </div>
            <textarea
              id="contribution-modal-notes"
              value={contributionModalForm.notes}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Optional notes"
              rows={3}
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 tw-resize-y"
            />
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowContributionModal(false);
                setContributionModalContext(null);
                setContributionModalEvidenceError(null);
              }}
            />
            <WButton
              variant="primary"
              label="Save"
              disabled={
                contributionModalContext?.type === 'add' &&
                !contributionModalForm.personaId
              }
              onClick={() => {
                const evidenceUrl =
                  contributionModalForm.evidenceUrl?.trim() ?? '';
                const evidenceError = validateEvidenceUrl(evidenceUrl);
                if (evidenceError) {
                  setContributionModalEvidenceError(evidenceError);
                  return;
                }
                setContributionModalEvidenceError(null);
                const payload = {
                  contribution: contributionModalForm.contributionName,
                  confidenceCategory: contributionModalForm.confidenceCategory,
                  confidencePattern: JSON.stringify({
                    evidenceUrl,
                    notes: contributionModalForm.notes?.trim() ?? '',
                  }),
                  personaId: contributionModalForm.personaId || undefined,
                };
                if (contributionModalContext?.type === 'edit') {
                  setContributions((prev) =>
                    prev.map((c, i) =>
                      i === contributionModalContext.index ? payload : c
                    )
                  );
                } else if (contributionModalContext?.type === 'add') {
                  setContributions((prev) => [...prev, payload]);
                }
                setShowContributionModal(false);
                setContributionModalContext(null);
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Unsaved add-item warning modal (shown when saving with an inline
          ISRC / Label / Catalogue Number input still open) */}
      <WModal
        isOpen={unsavedItemWarning !== null}
        onClose={() => setUnsavedItemWarning(null)}
        title="Unsaved item"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You have a{unsavedItemWarning === 'ISRC' ? 'n' : ''}{' '}
            <strong>{unsavedItemWarning}</strong> being entered. Please click{' '}
            <strong>Create</strong> to add it, or <strong>Cancel</strong>,
            before saving your changes.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="primary"
              label="Got it"
              onClick={() => setUnsavedItemWarning(null)}
            />
          </div>
        </div>
      </WModal>

      {/* Remove contribution confirmation modal */}
      <WModal
        isOpen={showRemoveContributionModal !== null}
        onClose={() => setShowRemoveContributionModal(null)}
        title="Remove Contribution"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Are you sure you want to remove{' '}
            <strong>
              {showRemoveContributionModal !== null
                ? capitalize(
                    contributions[showRemoveContributionModal]?.contribution ??
                      ''
                  )
                : ''}
            </strong>{' '}
            from this recording?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowRemoveContributionModal(null)}
            />
            <WButton
              variant="primary"
              label="Remove"
              onClick={() => {
                if (showRemoveContributionModal !== null) {
                  const removeIdx = showRemoveContributionModal;
                  setContributions((prev) =>
                    prev.filter((_, i) => i !== removeIdx)
                  );
                  if (
                    contributionModalContext?.type === 'edit' &&
                    contributionModalContext.index === removeIdx
                  ) {
                    setShowContributionModal(false);
                    setContributionModalContext(null);
                  } else if (
                    contributionModalContext?.type === 'edit' &&
                    contributionModalContext.index > removeIdx
                  ) {
                    setContributionModalContext({
                      type: 'edit',
                      index: contributionModalContext.index - 1,
                    });
                  }
                  setShowRemoveContributionModal(null);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Artist demote/remove modal */}
      <WModal
        isOpen={artistActionModalIndex !== null}
        onClose={() => {
          setArtistActionModalIndex(null);
          setArtistActionConfirm(null);
        }}
        title={
          artistActionConfirm === 'demote'
            ? 'Confirm Demote'
            : artistActionConfirm === 'remove'
            ? 'Confirm Remove'
            : 'Artist Action'
        }
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4"
      >
        {artistActionModalIndex !== null &&
          artists[artistActionModalIndex] &&
          (() => {
            const artist = artists[artistActionModalIndex];
            const idx = artistActionModalIndex;

            if (artistActionConfirm === 'demote') {
              return (
                <div className="tw-mt-4 tw-space-y-4">
                  <p className="tw-text-white tw-text-sm">
                    Are you sure you want to demote{' '}
                    <strong>{artist.name}</strong> to Non-Featured?
                  </p>
                  <div className="tw-flex tw-justify-end tw-gap-2">
                    <WButton
                      variant="secondary"
                      label="Cancel"
                      onClick={() => setArtistActionConfirm(null)}
                    />
                    <WButton
                      variant="primary"
                      label="Demote"
                      onClick={() => {
                        setDemotedArtists((prev) => [...prev, artist]);
                        setArtists((prev) => prev.filter((_, i) => i !== idx));
                        setArtistActionModalIndex(null);
                        setArtistActionConfirm(null);
                      }}
                    />
                  </div>
                </div>
              );
            }

            if (artistActionConfirm === 'remove') {
              return (
                <div className="tw-mt-4 tw-space-y-4">
                  <p className="tw-text-white tw-text-sm">
                    Are you sure you want to remove{' '}
                    <strong>{artist.name}</strong> from this recording?
                  </p>
                  <div className="tw-flex tw-justify-end tw-gap-2">
                    <WButton
                      variant="secondary"
                      label="Cancel"
                      onClick={() => setArtistActionConfirm(null)}
                    />
                    <WButton
                      variant="primary"
                      label="Remove"
                      onClick={() => {
                        setArtists((prev) => prev.filter((_, i) => i !== idx));
                        setArtistActionModalIndex(null);
                        setArtistActionConfirm(null);
                      }}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div className="tw-mt-4 tw-space-y-3">
                <p className="tw-text-white tw-text-sm">
                  What would you like to do with <strong>{artist.name}</strong>?
                </p>
                <div className="tw-flex tw-flex-row tw-gap-2">
                  <WButton
                    variant="secondary"
                    label="Demote to Non-Featured"
                    extraClasses="tw-flex-1 !tw-whitespace-normal !tw-h-auto tw-min-h-[32px] tw-py-1.5 tw-text-center"
                    onClick={() => setArtistActionConfirm('demote')}
                  />
                  <WButton
                    variant="secondary"
                    label="Remove from Recording"
                    extraClasses="tw-flex-1 !tw-whitespace-normal !tw-h-auto tw-min-h-[32px] tw-py-1.5 tw-text-center"
                    onClick={() => setArtistActionConfirm('remove')}
                  />
                </div>
                <div className="tw-flex tw-justify-end tw-pt-2 tw-border-t tw-border-gray-700">
                  <WButton
                    variant="secondary"
                    label="Cancel"
                    onClick={() => {
                      setArtistActionModalIndex(null);
                      setArtistActionConfirm(null);
                    }}
                  />
                </div>
              </div>
            );
          })()}
      </WModal>

      {/* Duplicate ISRC confirmation modal */}
      <WModal
        isOpen={pendingDuplicateIsrc !== null}
        onClose={() => {
          setPendingDuplicateIsrc(null);
          setPendingDuplicateMatches([]);
        }}
        title="Reassign ISRC"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-3">
            This ISRC is currently assigned to:
          </p>
          {pendingDuplicateMatches.length > 0 && (
            <div className="tw-mb-4 tw-space-y-1.5 tw-max-h-32 tw-overflow-y-auto">
              {pendingDuplicateMatches.map((m) => (
                <div
                  key={m.recordingId}
                  className="tw-flex tw-flex-col tw-bg-gray-800 tw-rounded tw-px-3 tw-py-2 tw-text-xs"
                >
                  <span className="tw-text-white tw-font-medium">
                    {m.recordingTitle || 'Untitled'}
                  </span>
                  {m.artistName && (
                    <span className="tw-text-gray-400">{m.artistName}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="tw-text-white tw-text-sm">
            Continuing will remove the ISRC from{' '}
            {pendingDuplicateMatches.length === 1
              ? 'that recording'
              : 'those recordings'}{' '}
            and assign it to{' '}
            <strong>{formData.title || 'this recording'}</strong>.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-5">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setPendingDuplicateIsrc(null);
                setPendingDuplicateMatches([]);
              }}
            />
            <WButton
              variant="primary"
              label="Continue"
              onClick={() => {
                if (pendingDuplicateIsrc) {
                  addMultipleValue(setIsrcs, pendingDuplicateIsrc);
                  // Track all recordings this ISRC is being reassigned from
                  if (pendingDuplicateMatches.length > 0) {
                    setConfirmedDuplicateIsrcs((prev) => ({
                      ...prev,
                      [pendingDuplicateIsrc]: pendingDuplicateMatches.map(
                        (m) => ({
                          recordingId: m.recordingId,
                          documentId: m.documentId,
                        })
                      ),
                    }));
                  }
                  setIsrcInputValue('');
                  setIsrcInputOpen(false);
                  setPendingDuplicateIsrc(null);
                  setPendingDuplicateMatches([]);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Create new artist modal (contract 4.4.2) */}
      <WModal
        isOpen={showCreateArtist}
        onClose={() => {
          setShowCreateArtist(false);
          setNewArtistAlias('');
          setNewArtistFeatured('fa');
          setNewArtistSource('discogs');
          setNewArtistSourceId('');
          setNewArtistIsGroup(false);
          setNewArtistMembers([]);
          setNewArtistExists(false);
          setNewArtistMemberSearch('');
          setNewArtistSourceLookupError(null);
        }}
        title="Create new artist"
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          {/* Source + Source ID — entered first to auto-populate stage name */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <div>
              <label
                htmlFor="create-artist-source"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Source
              </label>
              <select
                id="create-artist-source"
                value={newArtistSource}
                onChange={(e) => {
                  setNewArtistSource(e.target.value as typeof newArtistSource);
                  setNewArtistAlias('');
                  setNewArtistSourceId('');
                  setNewArtistSourceLookupError(null);
                }}
                className={`${SELECT_BASE_CLASS} ${selectTextClass(
                  !!newArtistSource,
                )}`}
              >
                <option value="discogs">Discogs</option>
                <option value="MB">MusicBrainz</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="create-artist-source-id"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Source ID
              </label>
              <input
                id="create-artist-source-id"
                type="text"
                value={newArtistSourceId}
                onChange={(e) => {
                  setNewArtistSourceId(e.target.value);
                  if (!e.target.value.trim()) {
                    setNewArtistAlias('');
                    setNewArtistSourceLookupError(null);
                  }
                }}
                placeholder={
                  newArtistSource === 'discogs'
                    ? 'e.g. 12345'
                    : 'Enter source ID'
                }
                className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
              />
            </div>
          </div>

          {/* Stage Name — auto-populated from source lookup, editable */}
          <div>
            <label
              htmlFor="create-artist-alias"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Stage Name
            </label>
            <div className="tw-relative">
              <input
                id="create-artist-alias"
                type="text"
                value={newArtistAlias}
                onChange={(e) => setNewArtistAlias(e.target.value)}
                placeholder={
                  newArtistSourceLookupLoading
                    ? 'Looking up artist...'
                    : 'Enter stage name'
                }
                className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
              />
              {newArtistSourceLookupLoading && (
                <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-xs">
                  Loading...
                </span>
              )}
            </div>
            {newArtistSourceLookupError && (
              <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                {newArtistSourceLookupError}
              </p>
            )}
            {newArtistExists && !newArtistSourceLookupError && (
              <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                Artist already exists in WMC database. Use Artist Search to add
                them
              </p>
            )}
            {newArtistSource !== 'discogs' &&
              newArtistSourceId.trim() &&
              !newArtistAlias && (
                <p className="tw-text-gray-500 tw-text-xs tw-mt-1">
                  Auto-lookup is only available for Discogs. Enter the stage
                  name manually.
                </p>
              )}
          </div>

          {/* Featured Status */}
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-2">
              Featured Status
            </span>
            <div className="tw-flex tw-gap-8">
              <WmcRadioOption
                name="new-artist-featured"
                value="fa"
                checked={newArtistFeatured === 'fa'}
                label="Featured"
                onChange={() => setNewArtistFeatured('fa')}
              />
              <WmcRadioOption
                name="new-artist-featured"
                value="ofa"
                checked={newArtistFeatured === 'ofa'}
                label="Other Featured"
                onChange={() => setNewArtistFeatured('ofa')}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-700">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowCreateArtist(false);
                setNewArtistAlias('');
                setNewArtistFeatured('fa');
                setNewArtistSource('discogs');
                setNewArtistSourceId('');
                setNewArtistIsGroup(false);
                setNewArtistMembers([]);
                setNewArtistExists(false);
                setNewArtistMemberSearch('');
                setNewArtistSourceLookupError(null);
              }}
            />
            <WButton
              variant="primary"
              label={newArtistSourceLookupLoading ? 'Looking up...' : 'Add'}
              disabled={
                !newArtistSourceId.trim() ||
                !newArtistAlias.trim() ||
                newArtistSourceLookupLoading ||
                newArtistExists
              }
              onClick={() => {
                if (newArtistAlias.trim() && newArtistSourceId.trim()) {
                  setArtists((prev) => [
                    ...prev,
                    {
                      name: newArtistAlias.trim(),
                      isFeatured: newArtistFeatured === 'fa',
                      featuredStatus: newArtistFeatured,
                      members:
                        newArtistMembers.length > 0
                          ? newArtistMembers
                          : undefined,
                      pendingCreate: {
                        source: newArtistSource,
                        sourceId: newArtistSourceId.trim(),
                      },
                    },
                  ]);
                  setNewArtistAlias('');
                  setNewArtistFeatured('fa');
                  setNewArtistSource('discogs');
                  setNewArtistSourceId('');
                  setNewArtistIsGroup(false);
                  setNewArtistMembers([]);
                  setNewArtistExists(false);
                  setNewArtistMemberSearch('');
                  setNewArtistSourceLookupError(null);
                  setShowCreateArtist(false);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Add existing artist modal (featured selection) */}
      <WModal
        isOpen={pendingArtistName !== null}
        onClose={() => {
          setPendingArtistName(null);
          setPendingArtistId(null);
          setPendingArtistFeatured('fa');
        }}
        title="Add artist"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
              Artist
            </span>
            <p className="tw-text-white tw-text-sm">
              {pendingArtistName ?? ''}
            </p>
          </div>
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-2">
              Featured Status
            </span>
            <div className="tw-flex tw-gap-8">
              <WmcRadioOption
                name="pending-artist-featured"
                value="fa"
                checked={pendingArtistFeatured === 'fa'}
                label="Featured"
                onChange={() => setPendingArtistFeatured('fa')}
              />
              <WmcRadioOption
                name="pending-artist-featured"
                value="ofa"
                checked={pendingArtistFeatured === 'ofa'}
                label="Other Featured"
                onChange={() => setPendingArtistFeatured('ofa')}
              />
            </div>
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setPendingArtistName(null);
                setPendingArtistId(null);
                setPendingArtistMembers([]);
                setPendingArtistFeatured('fa');
              }}
            />
            <WButton
              variant="primary"
              label="Add"
              onClick={() => {
                if (pendingArtistName) {
                  setArtists((prev) => [
                    ...prev,
                    {
                      name: pendingArtistName,
                      isFeatured: pendingArtistFeatured === 'fa',
                      featuredStatus: pendingArtistFeatured,
                      id: pendingArtistId ?? undefined,
                      members:
                        pendingArtistMembers.length > 0
                          ? pendingArtistMembers
                          : undefined,
                    },
                  ]);
                  setPendingArtistName(null);
                  setPendingArtistId(null);
                  setPendingArtistMembers([]);
                  setPendingArtistFeatured('fa');
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Change Release Modal */}
      <WModal
        isOpen={showChangeReleaseModal}
        onClose={() => {
          setShowChangeReleaseModal(false);
          resetReleaseModalState();
        }}
        title={
          releaseSource === 'Other' || releaseLookupNotFound
            ? 'Create and Link Release'
            : 'Change Linked Release'
        }
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh]"
      >
        <div className="tw-mt-4 tw-overflow-y-auto tw-max-h-[calc(90vh-120px)]">
          <div className="tw-mb-4">
            <label
              htmlFor="release-source-input"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Release Source
            </label>
            <select
              id="release-source-input"
              value={releaseSource}
              onChange={(e) => {
                const newSource = e.target.value as
                  | 'Discogs'
                  | 'Deezer'
                  | 'Spotify'
                  | 'MusicBrainz'
                  | 'PPL'
                  | 'Other';
                setReleaseSource(newSource);
                setReleaseId('');
                setSourceUrl('');
                setReleaseLookupError(null);
                setReleaseLookupSuccess(false);
                setReleaseLookupNotFound(false);
                setReleaseLookupNotLinked(false);
                setReleaseNotLinkedConfirmed(false);
                setReleaseSameAsCurrentError(false);
                if (newSource === 'Other' && !newReleaseTitle.trim()) {
                  // Pre-populate from the currently linked release when fields are empty
                  setNewReleaseTitle(formData.release || '');
                  setNewReleaseYear(formData.year || '');
                  setNewReleaseLabels([...labels]);
                  setNewReleaseCatNumbers([...catNumbers]);
                  setNewReleaseFormat(formData.format || '');
                  setNewReleaseType(formData.mediaType || '');
                }
              }}
              className={`${SELECT_BASE_CLASS} ${selectTextClass(
                !!releaseSource,
              )}`}
            >
              <option value="Discogs">Discogs</option>
              <option value="Deezer">Deezer</option>
              <option value="Spotify">Spotify</option>
              <option value="MusicBrainz">MusicBrainz</option>
              <option value="PPL">PPL</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {['Discogs', 'Deezer', 'Spotify', 'MusicBrainz', 'PPL'].includes(
            releaseSource
          ) ? (
            <div className="tw-mb-4">
              <label
                htmlFor="release-id-input"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Release ID
              </label>
              <div className="tw-flex tw-gap-2 tw-items-center">
                <input
                  id="release-id-input"
                  type="text"
                  value={releaseId}
                  onChange={(e) => {
                    setReleaseId(e.target.value);
                    setReleaseLookupError(null);
                    setReleaseLookupSuccess(false);
                    setReleaseLookupNotFound(false);
                    setReleaseLookupNotLinked(false);
                    setReleaseNotLinkedConfirmed(false);
                    setReleaseSameAsCurrentError(false);
                  }}
                  className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                  placeholder="Enter release ID"
                />
                <WButton
                  variant="secondary"
                  label={releaseLookupLoading ? 'Looking up…' : 'Lookup'}
                  disabled={
                    releaseLookupLoading ||
                    !releaseId.trim() ||
                    !userId ||
                    !row?.id
                  }
                  onClick={handleReleaseLookup}
                />
              </div>
              {releaseLookupError && (
                <p className="tw-text-westburyRed tw-text-xs tw-mt-2">
                  {releaseLookupError}
                </p>
              )}
              {releaseLookupNotFound && (
                <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                  <p className="tw-text-orange-400 tw-text-sm">
                    Release not found in WMC database, please provide details.
                  </p>
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-title"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Release Title
                      </label>
                      <input
                        id="change-release-notfound-title"
                        type="text"
                        value={newReleaseTitle}
                        onChange={(e) => setNewReleaseTitle(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                        placeholder="Enter release title"
                      />
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="change-release-notfound-year"
                          className="tw-block tw-text-white tw-text-sm tw-font-medium"
                        >
                          Year of Release
                        </label>
                        <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                          Optional
                        </span>
                      </div>
                      <input
                        id="change-release-notfound-year"
                        type="text"
                        value={newReleaseYear}
                        onChange={(e) => {
                          const v = e.target.value
                            .replace(/\D/g, '')
                            .slice(0, 4);
                          setNewReleaseYear(v);
                          setNewReleaseYearError(validateReleaseYear(v));
                        }}
                        className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                          newReleaseYearError
                            ? 'tw-border-red-500'
                            : 'tw-border-gray-600'
                        }`}
                        placeholder="YYYY"
                        maxLength={4}
                      />
                      {newReleaseYearError && (
                        <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                          {newReleaseYearError}
                        </p>
                      )}
                    </div>
                  </div>
                  <MultiValueAddField
                    label="Label (Multiple)"
                    addLabel="Add label"
                    placeholder="Enter label"
                    inputId="change-release-notfound-labels-input"
                    values={newReleaseLabels}
                    onRemove={(index) =>
                      setNewReleaseLabels((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    open={newLabelInputOpen}
                    setOpen={setNewLabelInputOpen}
                    value={newLabelInputValue}
                    setValue={setNewLabelInputValue}
                    commit={commitNewLabel}
                  />
                  <MultiValueAddField
                    label="Catalogue Number (Multiple)"
                    addLabel="Add catalogue number"
                    placeholder="Enter catalogue number"
                    inputId="change-release-notfound-catno-input"
                    values={newReleaseCatNumbers}
                    onRemove={(index) =>
                      setNewReleaseCatNumbers((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    open={newCatNoInputOpen}
                    setOpen={setNewCatNoInputOpen}
                    value={newCatNoInputValue}
                    setValue={setNewCatNoInputValue}
                    commit={commitNewCatNo}
                  />
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="change-release-notfound-format"
                          className="tw-block tw-text-white tw-text-sm tw-font-medium"
                        >
                          Format
                        </label>
                        <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                          Optional
                        </span>
                      </div>
                      <select
                        id="change-release-notfound-format"
                        value={newReleaseFormat}
                        onChange={(e) => setNewReleaseFormat(e.target.value)}
                        className={`${SELECT_BASE_CLASS} ${selectTextClass(
                          !!newReleaseFormat,
                        )}`}
                      >
                        <option value="">Select Format</option>
                        {availableFormats.map((format) => (
                          <option key={format} value={format}>
                            {format}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="change-release-notfound-type"
                          className="tw-block tw-text-white tw-text-sm tw-font-medium"
                        >
                          Type
                        </label>
                        <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                          Optional
                        </span>
                      </div>
                      <select
                        id="change-release-notfound-type"
                        value={newReleaseType}
                        onChange={(e) => setNewReleaseType(e.target.value)}
                        className={`${SELECT_BASE_CLASS} ${selectTextClass(
                          !!newReleaseType,
                        )}`}
                      >
                        <option value="">Select Type</option>
                        {availableTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {releaseLookupSuccess && (
                <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-flex-1">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Release Title
                      </label>
                      <input
                        type="text"
                        value={newReleaseTitle}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                    <div className="tw-w-full">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Year of Release
                      </label>
                      <input
                        type="text"
                        value={newReleaseYear}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Label (Multiple)
                    </label>
                    <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                      {newReleaseLabels.length > 0 ? (
                        newReleaseLabels.map((label, index) => (
                          <span
                            key={index}
                            className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                          >
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="tw-text-gray-500">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Catalogue Number (Multiple)
                    </label>
                    {newReleaseCatNumbers.length > 0 && (
                      <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                        {newReleaseCatNumbers.length > 0 ? (
                          newReleaseCatNumbers.map((catNo, index) => (
                            <span
                              key={index}
                              className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                            >
                              {catNo}
                            </span>
                          ))
                        ) : (
                          <span className="tw-text-gray-500">—</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-w-full">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Format
                      </label>
                      <input
                        type="text"
                        value={newReleaseFormat}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                    <div className="tw-w-full">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Type
                      </label>
                      <input
                        type="text"
                        value={newReleaseType}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="tw-space-y-4">
              <div>
                <label
                  htmlFor="new-release-source-url"
                  className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                >
                  Source
                </label>
                <input
                  id="new-release-source-url"
                  type="text"
                  value={sourceUrl}
                  onChange={(e) => {
                    setSourceUrl(e.target.value);
                  }}
                  className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                  placeholder="Provide release source details"
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-title"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Release Title
                  </label>
                  <input
                    id="new-release-title"
                    type="text"
                    value={newReleaseTitle}
                    onChange={(e) => setNewReleaseTitle(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                    placeholder="Enter release title"
                  />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-year"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Year of Release
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                  </div>
                  <input
                    id="new-release-year"
                    type="text"
                    value={newReleaseYear}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setNewReleaseYear(v);
                      setNewReleaseYearError(validateReleaseYear(v));
                    }}
                    className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                      newReleaseYearError
                        ? 'tw-border-red-500'
                        : 'tw-border-gray-600'
                    }`}
                    placeholder="YYYY"
                    maxLength={4}
                  />
                  {newReleaseYearError && (
                    <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                      {newReleaseYearError}
                    </p>
                  )}
                </div>
              </div>
              <MultiValueAddField
                label="Label (Multiple)"
                addLabel="Add label"
                placeholder="Enter label"
                inputId="new-release-labels-input"
                values={newReleaseLabels}
                onRemove={(index) =>
                  setNewReleaseLabels((prev) =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                open={newLabelInputOpen}
                setOpen={setNewLabelInputOpen}
                value={newLabelInputValue}
                setValue={setNewLabelInputValue}
                commit={commitNewLabel}
              />
              <MultiValueAddField
                label="Catalogue Number (Multiple)"
                addLabel="Add catalogue number"
                placeholder="Enter catalogue number"
                inputId="new-release-catno-input"
                values={newReleaseCatNumbers}
                onRemove={(index) =>
                  setNewReleaseCatNumbers((prev) =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                open={newCatNoInputOpen}
                setOpen={setNewCatNoInputOpen}
                value={newCatNoInputValue}
                setValue={setNewCatNoInputValue}
                commit={commitNewCatNo}
              />
              <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <label
                      htmlFor="new-release-format"
                      className="tw-block tw-text-white tw-text-sm tw-font-medium"
                    >
                      Format
                    </label>
                    <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                      Optional
                    </span>
                  </div>
                  <select
                    id="new-release-format"
                    value={newReleaseFormat}
                    onChange={(e) => setNewReleaseFormat(e.target.value)}
                    className={`${SELECT_BASE_CLASS} ${selectTextClass(
                      !!newReleaseFormat,
                    )}`}
                  >
                    <option value="">Select Format</option>
                    {availableFormats.map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <label
                      htmlFor="new-release-type"
                      className="tw-block tw-text-white tw-text-sm tw-font-medium"
                    >
                      Type
                    </label>
                    <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                      Optional
                    </span>
                  </div>
                  <select
                    id="new-release-type"
                    value={newReleaseType}
                    onChange={(e) => setNewReleaseType(e.target.value)}
                    className={`${SELECT_BASE_CLASS} ${selectTextClass(
                      !!newReleaseType,
                    )}`}
                  >
                    <option value="">Select Type</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {releaseLookupNotLinked && (
            <div
              className="tw-rounded-lg tw-border tw-border-orange-500/60 tw-bg-orange-500/10 tw-p-4"
              role="alert"
            >
              <p className="tw-text-orange-400 tw-text-sm tw-font-medium tw-mb-2">
                Confirm Release Linking
              </p>
              <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
                This release is already linked to a different recording.
              </p>
              <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
                Please double-check that you are linking it to the correct
                recording and not a different version.
              </p>
              <label
                htmlFor="change-release-not-linked-confirmed"
                className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
              >
                <input
                  id="change-release-not-linked-confirmed"
                  type="checkbox"
                  checked={releaseNotLinkedConfirmed}
                  onChange={(e) =>
                    setReleaseNotLinkedConfirmed(e.target.checked)
                  }
                  className="tw-mt-0.5 tw-rounded tw-border-gray-600 tw-bg-gray-700 tw-text-orange-500 focus:tw-ring-orange-500"
                />
                <span className="tw-text-sm tw-text-white">
                  I have read and understood this warning
                </span>
              </label>
            </div>
          )}
          {releaseSameAsCurrentError && (
            <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
              This recording is already linked to this release
            </p>
          )}
          {releaseChangeSubmitError && (
            <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
              {releaseChangeSubmitError}
            </p>
          )}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-600">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowChangeReleaseModal(false);
                resetReleaseModalState();
              }}
            />
            <WButton
              variant="primary"
              label={
                releaseChangeSubmitLoading
                  ? 'Saving…'
                  : releaseSource === 'Other' || releaseLookupNotFound
                  ? 'Create and Link Release'
                  : 'Change Linked Release'
              }
              disabled={
                releaseChangeSubmitLoading ||
                releaseSameAsCurrentError ||
                !!newReleaseYearError ||
                (releaseSource === 'Other' || releaseLookupNotFound
                  ? !newReleaseTitle.trim() ||
                    (releaseSource === 'Other' && !sourceUrl.trim())
                  : !releaseLookupSuccess ||
                    !lookupReleaseId ||
                    (releaseLookupNotLinked && !releaseNotLinkedConfirmed))
              }
              onClick={handleChangeReleaseSubmit}
            />
          </div>
        </div>
      </WModal>

      {/* Live N → Y note modal */}
      <WModal
        isOpen={showLiveToYesModal}
        onClose={() => {
          setShowLiveToYesModal(false);
          setLiveToYesNote('');
        }}
        title="Mark Recording as Live"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-4">
            Provide Live information for this recording. This will be stored as
            the live descriptor and appended to the Version field.
          </p>
          <label
            htmlFor="live-to-yes-note"
            className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
          >
            Live Information <span className="tw-text-red-400">(required)</span>
          </label>
          <textarea
            id="live-to-yes-note"
            value={liveToYesNote}
            onChange={(e) => setLiveToYesNote(e.target.value)}
            placeholder="e.g. Live in London, 1989"
            rows={3}
            className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 tw-resize-y"
          />
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowLiveToYesModal(false);
                setLiveToYesNote('');
              }}
            />
            <WButton
              variant="primary"
              label="Confirm"
              disabled={!liveToYesNote.trim()}
              onClick={() => {
                handleFieldChange('isLive', true);
                // Append the note to the Version field
                const note = liveToYesNote.trim();
                setVersions((prev) =>
                  prev.length > 0 ? [...prev, note] : [note]
                );
                setShowLiveToYesModal(false);
                // Don't clear liveToYesNote here — it's read by handleSaveRecordingDetails
                // to send as the "live" field value. Cleared on save/cancel.
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Revert Confirmation Modal */}
      <WModal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setRevertTarget(null);
        }}
        title="Confirm Revert"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Do you want to revert to the original value?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowRevertModal(false);
                setRevertTarget(null);
              }}
            />
            <WButton
              variant="primary"
              label="Revert"
              onClick={() => {
                if (revertTarget) {
                  handleRevert(revertTarget.type, revertTarget.field);
                }
              }}
            />
          </div>
        </div>
      </WModal>
    </>
  );
});
